import {
  BackEndValueType,
  ConditionSetType,
  DashboardState,
  EApiContentType,
  ECardinality,
  ECardinalityConversion,
  EDatasourceType,
  EProjectType,
  EPropertyType,
  ESymbolCategory,
  EWFStepType,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IDatabaseTableModel,
  IDesign,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IDocumentParameterInput,
  IEvent,
  IGenericSelectTablesModel,
  IKType,
  IKTypeField,
  IMethodInput,
  IProperty,
  IPropertyDefinition,
  IScreenDesign,
  ISymbol,
  ITypeElementModel,
  ITypeModel,
  IWFNodes,
  isComplexObjectParameter
} from "@kuika/kuika-cl-model";
import { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { isInteractionNotifiable } from "../../components/design-tools/flows/process-editor/forms-tab-steps";
import {
  getBpmnLoopState,
  LOOP_ITERATION_KEY
} from "../../components/design-tools/right-panel/workflow-panel/wf-utils/loop-utils";
import {
  getBpmnMiState,
  MI_COMPLETED_COUNT_KEY,
  MI_INSTANCE_COUNT_KEY
} from "../../components/design-tools/right-panel/workflow-panel/wf-utils/multi-instance-utils";
import { KuikaTag } from "../../components/ui-kit/kuika-tag/kuika-tag";
import { IWithSDProps } from "../hoc/screen-design-consumer";
import { AttachablePlatformContext, isActionDefinitionPlatformCompatible } from "./bindables/attachable-actions";
import { createActionResultsLoadingItem } from "./bindables/cascader-loading-item";
import { isRowSourceOptionsProperty } from "./current-row-source";
import { isDesignerActionEnabled } from "./designer-action-actions";
import { KMainFunctions } from "./main-functions";
import { PropertySdHelper } from "./property-sd-helper";
import { ScreenDesignHelper } from "./screen-design-helper";

/** Create a minimal IKType from just a type name (for generateCardinalityConversion). */
function toKType(name: string): IKType {
  return { id: 0, name };
}

export interface ISymbolPickerCascaderProps {
  targetTypeName?: string;
  targetCardinality?: ECardinality;
  onSymbolPicked?: (symbol: ISymbol) => void;
  targetDI?: IDisplayItem;
  categoryName: string;
  symbol?: ISymbol;
  hideFixedValue?: boolean;
  hideWFParams?: boolean;
  action?: IAction;
  parameter?: IActionParameter;
  /**
   * Preview-only fallback for the "Binding to" header (symbol-picker-host-context.ts) when a host
   * edits a named action parameter's symbol but does not pass `parameter`. The engine itself never
   * reads this — only `parameter` drives option generation, so this cannot change the option tree.
   */
  bindingParameter?: IActionParameter;
  /** Overrides the preview header's name; see `SymbolPicker`'s `bindingLabel` doc. */
  bindingLabel?: string;
  hideActionResults?: boolean;
  /**
   * KOA-17739: opt-in for the "Condition" category (conditional parameter value,
   * `condition ? trueSymbol : falseSymbol`). Set only by action-parameter hosts in the screen
   * designer; the pickers nested inside the condition editor never set it, so conditions
   * cannot nest.
   */
  allowConditionValue?: boolean;
  showInSingleLine?: boolean;
  /**
   * Fix the popup's box and scroll its columns rather than letting it grow and be repositioned. Opt-in, for callers
   * with room to their right; panel pickers are pinned by `.sticky-symbol-picker` and keep opening leftwards. The
   * columns popup (`symbolPickerColumnsV2`) opens compact for it, as it does inside a modal or a popover.
   */
  stablePopup?: boolean;
  isGenericSelect?: boolean;
  isObject?: boolean;
  children?: React.ReactNode;
  pickerIcon?: React.ReactNode;
  popupPlacement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  disabled?: boolean;
  isConditionPicker?: boolean;
  data_testid?: string;
  hideMiInstanceCountParam?: boolean;
  property?: IProperty;

  genericSelectTables?: IGenericSelectTablesModel[];
  genericSelectTableModels?: IDatabaseTableModel[];
}

enum EDefaultValueType {
  DateNow,
  Empty,
  Space,
  Null,
  True,
  False,
  NewGuid,
  EmptyGuid,
  GetMyUsername,
  CurrentUserFirstName,
  CurrentUserLastName,
  CurrentUserPhoneNumber,
  CurrentUserRoleNames,
  CurrentUserFullName,
  GetCurrentLocation,
  GetVersionInfo,
  GetProcessInitiator,
  ClientIpAddress,
  CustomBinding,
  CurrentUserid,
  IsMicroApp,
  CurrentPageNumber,
  TotalPageNumber,
  CurrentURL
}

export const knownTypes: string[] = [
  "boolean",
  "bytea",
  "byte",
  "date",
  "datetime",
  "datetimeoffset",
  "decimal",
  "double",
  "guid",
  "int16",
  "int32",
  "int64",
  "single",
  "string",
  "image",
  "number",
  "object",
  "colorpicker",
  "any",
  "iformcollection"
];

/**
 * KOA-17739: whether the picker offers the "Condition" category (conditional parameter value).
 * Deterministic surface policy: action-parameter hosts opt in via `allowConditionValue`, the
 * screen designer is the only host dashboard (web-only scope), the pickers nested inside a
 * condition never offer it, and only scalar known types qualify — mirroring "Fixed Value".
 */
export function shouldOfferConditionValueOption(
  props: ISymbolPickerCascaderProps & IWithSDProps,
  targetType: string,
  targetCardinality: ECardinality
): boolean {
  return (
    props.allowConditionValue === true &&
    props.isConditionPicker !== true &&
    props.sd.dashboardState === DashboardState.design &&
    knownTypes.includes(targetType.toLowerCase()) &&
    targetCardinality !== ECardinality.Many
  );
}

export interface IDesignerSymbolPickerOption {
  path: number[];
  label: string;
  category?: string;
  description?: string;
  currentType?: string;
  targetType?: string;
  domainType?: string;
  cardinality?: ECardinality;
  diId?: number;
  inputName?: string;
  fieldName?: string;
  resultActionId?: string;
  actionDefinitionId?: string;
  defaultValueName?: string;
  isLeaf: boolean;
  children?: IDesignerSymbolPickerOption[];
}

export class DesignerSymbolPickerEngine {
  constructor(private readonly props: ISymbolPickerCascaderProps & IWithSDProps) {}
  getIsNestedDataBindingEnabled = (): boolean => {
    return (
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.workFlow ||
      this.props.sd.dashboardState === DashboardState.apiManagement
    );
  };

  generateItems = (
    targetType: string = "String",
    targetCardinality: ECardinality = ECardinality.One
  ): CascaderOptionType[] => {
    targetType = this.toCamelCase(targetType);

    if (this.props.sd.isDocumentParameterAuthoringActive()) {
      return this.generateDocumentParameterItems(targetType, targetCardinality);
    }

    const items: CascaderOptionType[] = [];
    const defaultItem = this.generateDefaultItems(targetType);

    if (knownTypes.includes(targetType.toLowerCase()) && targetCardinality === ECardinality.Many)
      defaultItem.children = defaultItem.children?.filter((c) => c.defaultValueType === EDefaultValueType.Null);

    if (defaultItem.children && defaultItem.children.length > 0) {
      if (this.props.sd.dashboardState === DashboardState.workFlow) {
        const currentUserIndex = defaultItem.children.findIndex(
          (i) => i.defaultValueType === EDefaultValueType.GetMyUsername
        );
        if (currentUserIndex !== -1) defaultItem.children.splice(currentUserIndex, 1);
      }

      if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
        defaultItem.children = defaultItem.children.filter(
          (i) => i.defaultValueType !== EDefaultValueType.ClientIpAddress
        );
      }

      items.push(defaultItem);
    }

    if (this.isGetMyTasksSortParam()) {
      const sortGroup: CascaderOptionType = {
        category: ESymbolCategory.default,
        label: "Sort Keys",
        children: this.getMyTasksSortKeys().map(
          (sk) =>
            ({
              category: ESymbolCategory.fixed,
              label: sk.label,
              isFixed: true,
              isCustomSortKey: true,
              sortKey: sk.value
            }) as CascaderOptionType
        )
      };
      items.push(sortGroup);
    }

    if (
      (knownTypes.includes(targetType.toLowerCase()) || this.props.parameter?.isEnum) &&
      this.props.hideFixedValue !== true
    ) {
      const fixed = { category: ESymbolCategory.fixed, label: "Fixed Value", isFixed: true } as CascaderOptionType;
      if (this.props.parameter?.isEnum || targetCardinality !== ECardinality.Many) {
        items.push(fixed);
      } else if (knownTypes.includes(targetType.toLowerCase()) && targetCardinality === ECardinality.Many) {
        items.push(fixed);
      }
    }

    // KOA-17739: conditional parameter value ("condition ? trueValue : falseValue"). Offered only
    // to action-parameter hosts (allowConditionValue) in the screen designer, for scalar known
    // types — honoured by the web (React) generator.
    if (shouldOfferConditionValueOption(this.props, targetType, targetCardinality)) {
      items.push({ category: ESymbolCategory.condition, label: "Condition" } as CascaderOptionType);
    }

    // VTO: this if control will be removed after complex type workflow parameters supported.
    if (!isComplexObjectParameter(this.props.parameter)) {
      const wfItems = this.generateWorkFlowBagItems(targetType, targetCardinality);

      if (wfItems.children && wfItems.children.length > 0 && this.props.hideWFParams !== true) {
        items.push(wfItems);
      }
    }

    if (this.props.sd.dashboardState === DashboardState.workFlow) {
      const customActionItem = this.generateActionResultItems(targetType, targetCardinality);
      customActionItem.children = customActionItem.children?.filter((x) => x.label);

      if (customActionItem.children && customActionItem.children.length > 0) {
        items.push(customActionItem);
      }
    }

    if (!this.props.isGenericSelect) {
      const bleMethod = this.props.sd.getSelectedBluetoothMethod();

      if (bleMethod && this.props.sd.dashboardState === DashboardState.dataDiagramScheme) {
        items.push({
          category: ESymbolCategory.bluetoothValue,
          label: "Bluetooth Value",
          description: "Bluetooth Value",
          defaultValueName: "bluetoothValue",
          currentType: "String",
          defaultValueType: "String"
        });
      }

      if (this.props.sd.dashboardState === DashboardState.workFlow) {
        items.forEach((element) => {
          this.setKeyOfNodeTree(element);
        });

        return items;
      }

      const componentsOption: CascaderOptionType = this.generateDiItems(targetType, targetCardinality);
      const parentParam = this.getParentParameter();
      // KOA-18737: a raw-JSON (JObject / JArray / JToken) parameter is bound as one value, so it keeps
      // the Components group even when a pre-fix definition stored it as an object.
      if (
        isComplexObjectParameter(this.props.parameter) ||
        this.props.parameter?.cardinality === ECardinality.Many ||
        (parentParam && parentParam.cardinality === ECardinality.Many) ||
        this.props.parameter?.isEnum
      )
        // if (
        //   this.props.parameter?.isObject ||
        //   (this.props.parameter?.cardinality === ECardinality.Many &&
        //     (this.props.parameter?.isObject ||
        //       this.props.parameter?.isEnum ||
        //       this.props.parameter?.ownerPropName !== undefined))
        // )
        componentsOption.children = [];

      if (
        componentsOption.children &&
        componentsOption.children.length > 0 &&
        this.props.sd.dashboardState !== DashboardState.reportDesigner
      ) {
        items.push(componentsOption);
      }

      const roleListOption: CascaderOptionType = this.generateRoleList();

      if (
        roleListOption.children &&
        roleListOption.children.length > 0 &&
        targetType.toLowerCase() === "string" &&
        targetCardinality === ECardinality.One
      ) {
        items.push(roleListOption);
      }

      if (!this.props.hideActionResults) {
        const actionResultsOption: CascaderOptionType = this.generateActionResultItems(targetType, targetCardinality);
        actionResultsOption.children = actionResultsOption.children?.filter((x) => x.label);

        if (actionResultsOption.children && actionResultsOption.children.length > 0) {
          items.push(actionResultsOption);
        }
      }

      if (!this.props.sd.project.processStepScreenIds?.find((p) => p.screenId === this.props.sd.sdId)) {
        const screenInputsOption: CascaderOptionType = this.generateScreenInputItems(targetType, targetCardinality);

        if (
          screenInputsOption.children &&
          screenInputsOption.children.length > 0 &&
          this.props.sd.dashboardState !== DashboardState.scheduledJobs
        ) {
          items.push(screenInputsOption);
        }
      }

      // PostData option for form-encoded API requests
      if (this.props.sd.dashboardState === DashboardState.apiManagement) {
        const selectedApiMethod = this.props.sd.getSelectedApiMethod();
        if (selectedApiMethod?.contentType === EApiContentType.FormUrlEncoded) {
          items.push({
            category: ESymbolCategory.postData,
            label: "Post Data",
            description: "postData (IFormCollection)",
            isLeaf: true,
            currentType: "IFormCollection",
            domainType: { name: "IFormCollection" }
          } as CascaderOptionType);
        }
      }

      if (["Image"].includes(targetType)) {
        const uploadedImages: CascaderOptionType = {
          category: ESymbolCategory.uploadedImages,
          label: "Uploaded Images"
        };
        uploadedImages.isFixed = true;
        items.push(uploadedImages);
      }

      const bagsOption: CascaderOptionType = this.generateBagItems(targetType, targetCardinality);

      if (bagsOption.children && bagsOption.children.length > 0) {
        items.push(bagsOption);
      }

      if (
        this.props.sd.dashboardState === DashboardState.design ||
        this.props.sd.dashboardState === DashboardState.reportDesigner
      ) {
        const sd: IScreenDesign = this.props.sd.getScreenDesign();

        let di = this.props.targetDI;
        if (!di) di = this.props.sd.getSelectedDisplayItem(sd);
        const currentOption: CascaderOptionType = di
          ? this.generateCurrentRow(di, targetType, targetCardinality)
          : (null as unknown as CascaderOptionType);

        if (currentOption?.children && currentOption.children.length > 0) {
          if (this.props.sd.project.projectType === EProjectType.Desktop && targetType === "Any") {
            const directCurrent = { ...currentOption };
            delete directCurrent.children;
            items.push(directCurrent);
            items.push(currentOption);
          } else {
            items.push(currentOption);
          }
        }

        const forEachCurrentOption = di
          ? this.generateForEachCurrentRow(di, targetType, targetCardinality)
          : (null as unknown as CascaderOptionType);

        if (forEachCurrentOption?.children && forEachCurrentOption.children.length > 0) {
          if (this.props.sd.project.projectType === EProjectType.Desktop && targetType === "Any") {
            const directCurrent = { ...forEachCurrentOption };
            delete directCurrent.children;
            items.push(directCurrent);
            items.push(forEachCurrentOption);
          } else {
            items.push(forEachCurrentOption);
          }
        }
      }
    } else {
      items.push({ category: ESymbolCategory.sqlParameter, label: "SQL Parameter" } as CascaderOptionType);
    }

    // Add table fields for generic select calculated fields
    if (this.props.isGenericSelect && this.props.genericSelectTables && this.props.genericSelectTableModels) {
      const tableFieldsItem: CascaderOptionType = {
        category: ESymbolCategory.actionField,
        label: "Table Fields",
        description: "Fields from joined tables",
        children: []
      };

      this.props.genericSelectTables.forEach((table) => {
        const tableModel = this.props.genericSelectTableModels?.find((t) => t.name === table.name);
        if (tableModel && tableModel.columns) {
          const tableDisplayName = table.alias || table.name;
          const tableOption: CascaderOptionType = {
            category: ESymbolCategory.actionField,
            label: tableDisplayName,
            description: `Table: ${tableDisplayName}`,
            children: []
          };

          tableModel.columns.forEach((column) => {
            // Filter fields based on targetTypeName if it requires datetime/datetimeoffset
            const targetType = this.props.targetTypeName?.toLowerCase();
            const columnType = column.kuikaDataType?.toLowerCase();

            // If targetType is datetime or datetimeoffset, only show compatible date/time fields
            if (targetType === "datetime" || targetType === "datetimeoffset") {
              // Only show datetime, datetimeoffset, date, time types
              const allowedTypes = ["datetime", "datetimeoffset", "date", "time"];
              if (!allowedTypes.includes(columnType || "")) {
                return; // Skip this column
              }
            }

            tableOption.children?.push({
              category: ESymbolCategory.actionField,
              label: column.name,
              description: `${tableDisplayName}.${column.name}`,
              fieldName: `${tableDisplayName}.${column.name}`,
              fieldType: column.kuikaDataType,
              currentType: column.kuikaDataType,
              toType: column.kuikaDataType
            } as CascaderOptionType);
          });

          if (tableOption.children && tableOption.children.length > 0) {
            tableFieldsItem.children?.push(tableOption);
          }
        }
      });

      if (tableFieldsItem.children && tableFieldsItem.children.length > 0) {
        items.push(tableFieldsItem);
      }
    }

    items.forEach((element) => {
      this.setKeyOfNodeTree(element);
    });

    return items;
  };

  setKeyOfNodeTree = (node: CascaderOptionType) => {
    node.value = Guid.create().toString();

    if (node.children) {
      node.children.forEach((element) => {
        this.setKeyOfNodeTree(element);
      });
    }
  };

  getBackwardsCompatibleDiItemLabel = (displayItem: IDisplayItem): string => {
    if (displayItem.friendlyName) {
      return displayItem.friendlyName;
    }

    if (typeof displayItem.item !== "string") {
      if (displayItem.item.name) {
        return displayItem.item.name;
      }

      return displayItem.item.id;
    }

    return displayItem.item;
  };

  getBackwardsCompatibleDiId = (di: IDisplayItem) => {
    if (di.id) {
      return di.id;
    }

    if (typeof di.item !== "string") {
      if (di.item.name) {
        return di.item.name;
      }

      return di.item.id;
    }

    return di.item;
  };

  injectCascaderOptionsAccordingToDataSource = (
    di: IDisplayItem,
    datasourcePropDef: IPropertyDefinition,
    diCascaderOption: CascaderOptionType,
    targetTypeName: string
  ) => {
    console.assert(di !== undefined, "Display item is not defined");
    console.assert(datasourcePropDef !== undefined, "Datasource type is not defined");
    console.assert(diCascaderOption !== undefined, "Cascader option is not defined");

    if (datasourcePropDef.isMenuOptionsProp === true) {
      const fieldOfTypeItem: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "selectedKey",
        fieldtype: "k_selectedKey",
        diPropName: "k_selectedKey",
        field: {
          name: "k_selectedKey"
        }
      };

      diCascaderOption.children?.push(fieldOfTypeItem);
    }

    if (datasourcePropDef.isGoogleMapsOptionsProp === true) {
      const fieldOfTypeItem: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Latitude",
        fieldtype: "k_latitude",
        diPropName: "k_latitude",
        field: {
          name: "k_latitude"
        },
        domainType: {
          name: "String"
        },
        children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
      };

      diCascaderOption.children?.push(fieldOfTypeItem);
    }

    if (datasourcePropDef.isGoogleMapsOptionsProp === true) {
      const fieldOfTypeItem: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Longitude",
        fieldtype: "k_longitude",
        diPropName: "k_longitude",
        field: {
          name: "k_longitude"
        },
        domainType: {
          name: "String"
        },
        children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
      };

      diCascaderOption.children?.push(fieldOfTypeItem);
    }

    if (datasourcePropDef.isGoogleMapsOptionsProp === true) {
      const fieldOfTypeItem: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "CircleRadius",
        fieldtype: "k_circleRadius",
        diPropName: "k_circleRadius",
        field: {
          name: "k_circleRadius"
        },
        domainType: {
          name: "Decimal"
        },
        children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
      };

      diCascaderOption.children?.push(fieldOfTypeItem);
    }
  };

  getShouldSkipPropsOfComponent = (diItem: IDisplayItemComponent): boolean => {
    return diItem.name === "Calendar";
  };

  injectCascaderOptionsAccordingToComponent = (
    di: IDisplayItem,
    diCascaderOption: CascaderOptionType,
    targetType: string
  ) => {
    if (di.item.name == "PlacesAutocomplete" || di.item.name === "LocationSearch") {
      const fieldOfTypeItemLatitude: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Latitude",
        fieldtype: "k_latitude",
        diPropName: "k_latitude",
        field: {
          name: "k_latitude"
        },
        domainType: {
          name: "String"
        },
        children: ScreenDesignHelper.generateTypeConversion("string", targetType)
      };

      const fieldOfTypeItemLongitude: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Longitude",
        fieldtype: "k_longitude",
        diPropName: "k_longitude",
        field: {
          name: "k_longitude"
        },
        domainType: {
          name: "String"
        },
        children: ScreenDesignHelper.generateTypeConversion("string", targetType)
      };

      const fieldOfTypeItemAddress: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Address",
        fieldtype: "k_address",
        diPropName: "k_address",
        field: {
          name: "k_address"
        },
        domainType: {
          name: "String"
        },
        children: ScreenDesignHelper.generateTypeConversion("string", targetType)
      };

      diCascaderOption.children?.push(fieldOfTypeItemLatitude, fieldOfTypeItemLongitude, fieldOfTypeItemAddress);
    }

    if (di.item.name === "LocationPicker") {
      const fieldOfTypeItemLatitude: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Latitude",
        fieldtype: "k_latitude",
        diPropName: "k_latitude",
        field: {
          name: "k_latitude"
        },
        domainType: {
          name: "Decimal"
        },
        children: ScreenDesignHelper.generateTypeConversion("decimal", targetType)
      };

      const fieldOfTypeItemLongitude: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "Longitude",
        fieldtype: "k_longitude",
        diPropName: "k_longitude",
        field: {
          name: "k_longitude"
        },
        domainType: {
          name: "Decimal"
        },
        children: ScreenDesignHelper.generateTypeConversion("decimal", targetType)
      };

      diCascaderOption.children?.push(fieldOfTypeItemLatitude, fieldOfTypeItemLongitude);
    }

    if (di.item.name === "Menu") {
      const fieldOfTypeItemSelectedKey: CascaderOptionType = {
        category: ESymbolCategory.displayItemProp,
        label: "selectedKey",
        fieldtype: "k_selectedKey",
        diPropName: "k_selectedKey",
        field: {
          name: "k_selectedKey"
        }
      };

      diCascaderOption.children?.push(fieldOfTypeItemSelectedKey);
    }

    if (di.item.name === "Calendar") {
      diCascaderOption.children = [
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentDate",
          fieldtype: "k_currentDate",
          diPropName: "k_currentDate",
          field: {
            name: "k_currentDate"
          },
          children: ScreenDesignHelper.generateTypeConversion("string", targetType)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentDay",
          fieldtype: "k_currentDay",
          diPropName: "k_currentDay",
          field: {
            name: "k_currentDay"
          },
          children: ScreenDesignHelper.generateTypeConversion("int16", targetType)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentMonth",
          fieldtype: "k_currentMonth",
          diPropName: "k_currentMonth",
          field: {
            name: "k_currentMonth"
          },

          children: ScreenDesignHelper.generateTypeConversion("int16", targetType)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentYear",
          fieldtype: "k_currentYear",
          diPropName: "k_currentYear",
          field: {
            name: "k_currentYear"
          },
          children: ScreenDesignHelper.generateTypeConversion("int16", targetType)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentEvent",
          fieldtype: "k_currentEvent",
          diPropName: "k_currentEvent",
          field: {
            name: "k_currentEvent"
          },
          children: [
            {
              category: ESymbolCategory.fieldOfType,
              label: "id",
              fieldtype: "k_eventId",
              diPropName: "k_eventId",
              field: {
                name: "k_eventId"
              },
              children: ScreenDesignHelper.generateTypeConversion("guid", targetType)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "title",
              fieldtype: "k_eventTitle",
              diPropName: "k_eventTitle",
              field: {
                name: "k_eventTitle"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetType)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "startDate",
              fieldtype: "k_eventStartDate",
              diPropName: "k_eventStartDate",
              field: {
                name: "k_eventStartDate"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetType)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "endDate",
              fieldtype: "k_eventEndDate",
              diPropName: "k_eventEndDate",
              field: {
                name: "k_eventEndDate"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetType)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "color",
              fieldtype: "k_eventColor",
              diPropName: "k_eventColor",
              field: {
                name: "k_eventColor"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetType)
            }
          ]
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "panel",
          fieldtype: "k_panel",
          diPropName: "k_panel",
          field: {
            name: "k_panel"
          },
          children: ScreenDesignHelper.generateTypeConversion("string", targetType)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentRangeStart",
          fieldtype: "k_currentRangeStart",
          diPropName: "k_currentRangeStart",
          field: {
            name: "k_currentRangeStart"
          },
          children: ScreenDesignHelper.generateTypeConversion("datetimeoffset", targetType)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentRangeEnd",
          fieldtype: "k_currentRangeEnd",
          diPropName: "k_currentRangeEnd",
          field: {
            name: "k_currentRangeEnd"
          },
          children: ScreenDesignHelper.generateTypeConversion("datetimeoffset", targetType)
        }
      ];
    }
  };

  generateDiItems = (targetType: string, targetCardinality: ECardinality, isMaster?: boolean): CascaderOptionType => {
    let sd: IScreenDesign | undefined = this.props.sd.getScreenDesign();

    if (isMaster) {
      sd = this.props.sd.masterSd;
    }

    const allDi: IDisplayItem[] = [];

    if (sd) {
      this.fillAllDisplayItemsRecursive(sd, allDi);
    }

    const itemRenderer: CascaderOptionType = {
      category: ESymbolCategory.components,
      label: "Components",
      value: "Components",
      children: []
    };

    if (this.props.action?.label === "ForEach") return itemRenderer;

    for (let j = 0; j < allDi.length; j++) {
      const di: IDisplayItem = allDi[j];
      const skipProps: boolean = this.getShouldSkipPropsOfComponent(di.item);

      const diCascaderOption: CascaderOptionType = {
        category: ESymbolCategory.displayItem,
        label: this.getBackwardsCompatibleDiItemLabel(di),
        diId: this.getBackwardsCompatibleDiId(di),
        children: []
      };

      this.injectCascaderOptionsAccordingToComponent(di, diCascaderOption, targetType);

      const props: IDisplayItemProperty[] = di.item.properties;

      for (const prop of props) {
        if (skipProps) continue;
        if (!prop) continue;

        const propDef = this.props.sd.getPropertyDefinition(di.item, prop);
        if (!propDef) continue;

        if (prop.name === "dataSource") {
          this.injectCascaderOptionsAccordingToDataSource(di, propDef, diCascaderOption, targetType);
        }

        if (propDef.isVisibleInFieldPicker !== true) continue;

        const option: CascaderOptionType = {
          // variableDesignator: key,
          di,
          label: prop.name,
          domainType: this.calculateValueTypeByDi(di, prop),
          cardinality: this.calculateCardinalityByRendererNameAndVariableName(prop, di.item),
          category: ESymbolCategory.displayItemProp,
          friendlyVariableName: prop.name
        };

        if (option.domainType && option.domainType.name !== "Unbound") {
          option.children = this.generateCardinalityConversion(
            option.domainType,
            option.cardinality!,
            targetType,
            targetCardinality,
            prop,
            di.item
          );
        }

        const typeName: string = option.domainType?.name ?? option.domainType ?? "";

        if (
          (option.children && option.children.length > 0) ||
          ScreenDesignHelper.getAreTypesCompatible(typeName, targetType)
        ) {
          diCascaderOption.children?.push(option);

          if (propDef && propDef.isValueProp === true) {
            const showSearch = props.find((p) => p.name === "showSearch");
            const isAutoCompleteComponent: boolean = di.item.name === "AutoComplete";
            // The panel's toggle persists into symbol.fixedValue (what codegen reads); `value` is
            // only the metadata default, so checking it alone never saw a turned-on switch.
            const showSearchSetting: unknown = showSearch?.symbol?.fixedValue ?? showSearch?.value;
            const isShowSearchActive: boolean = showSearchSetting === true || showSearchSetting === "true";
            // A MultiSelect always has a search input (antd multiple mode) and its showSearch
            // prop is technical/hidden, so the On Search text must be offered unconditionally.
            const isMultiSelectComponent: boolean = di.item.name === "MultiSelect";
            // Directory exposes a showSearch prop too, but its search is a client-side
            // filter with no bindable "Search Value" symbol — don't offer it there.
            const isDirectoryComponent: boolean = di.item.name === "Directory";

            if (
              diCascaderOption.children?.find((x) => x.label === "Search Value") === undefined &&
              !isDirectoryComponent &&
              (isShowSearchActive || isAutoCompleteComponent || isMultiSelectComponent)
            ) {
              const fieldOfTypeItem = {
                category: ESymbolCategory.fieldOfType,
                label: "Search Value",
                fieldtype: "k_searchValue",
                field: {
                  name: "k_searchValue"
                }
              } as CascaderOptionType;
              diCascaderOption.children?.push(fieldOfTypeItem);
            }
          }
        }
      }

      if (
        (diCascaderOption.children &&
          diCascaderOption.children.length > 0 &&
          this.props.sd.dashboardState !== DashboardState.scheduledJobs &&
          this.props.sd.dashboardState !== DashboardState.apiManagement &&
          this.props.sd.dashboardState !== DashboardState.workFlow &&
          this.props.sd.dashboardState !== DashboardState.dataDiagramScheme) ||
        (this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
          this.props.sd.activeEditingDatasource?.datasourceType !== EDatasourceType.BleService &&
          this.props.sd.activeCreatingDatasourceType !== EDatasourceType.BleService)
      ) {
        itemRenderer.children?.push(diCascaderOption);
      }
    }

    if (itemRenderer.children) {
      itemRenderer.children = itemRenderer.children.sort((a, b) => {
        if (a.label && b.label) {
          return (a.label as string).toLowerCase().localeCompare((b.label as string).toLowerCase());
        }
        return 0;
      });
    }

    return itemRenderer;
  };

  getMasterScreenList = (targetType: string, targetCardinality: ECardinality): CascaderOptionType => {
    const generatedDiItems = this.generateDiItems(targetType, targetCardinality, true);
    const generatedCustomActionItem = this.generateActionResultItems(targetType, targetCardinality, true);
    const generatedScreenInputItems = this.generateScreenInputItems(targetType, targetCardinality, true);

    const itemRenderer: CascaderOptionType = {
      category: ESymbolCategory.masterScreen,
      label: "Master Screen",
      value: "Master Screen",
      children: []
    };

    if (generatedDiItems?.children && generatedDiItems.children.length > 0) {
      itemRenderer.children?.push(generatedDiItems);
    }

    if (generatedCustomActionItem?.children && generatedCustomActionItem.children.length > 0) {
      itemRenderer.children?.push(generatedCustomActionItem);
    }

    if (generatedScreenInputItems?.children && generatedScreenInputItems.children.length > 0) {
      itemRenderer.children?.push(generatedScreenInputItems);
    }

    return itemRenderer;
  };

  getNestedActions = (action: IAction): IAction[] => {
    const result: IAction[] = [];
    const queue: IAction[] = [action];
    const processedIds = new Set<string>();

    while (queue.length > 0) {
      const currentAction = queue.shift();
      if (!currentAction || !currentAction.id || processedIds.has(currentAction.id)) continue;
      if (!isDesignerActionEnabled(currentAction)) continue;

      processedIds.add(currentAction.id);

      if (currentAction !== action) {
        result.push(currentAction);
      }

      if (currentAction.forEachData) {
        currentAction.forEachData.actions?.forEach((action) => {
          if (action.id && !processedIds.has(action.id)) {
            queue.push(action);
          }
        });
      }

      if (currentAction.ifThenElseData) {
        currentAction.ifThenElseData.ifActions?.forEach((action) => {
          if (action.id && !processedIds.has(action.id)) {
            queue.push(action);
          }
        });

        currentAction.ifThenElseData.elseActions?.forEach((action) => {
          if (action.id && !processedIds.has(action.id)) {
            queue.push(action);
          }
        });
      }

      if (currentAction.recurringActionsData) {
        currentAction.recurringActionsData.recurringActions?.forEach((action) => {
          if (action.id && !processedIds.has(action.id)) {
            queue.push(action);
          }
        });
      }

      if (currentAction.mqttSubscribeActionData?.mqttSubscribeActions) {
        currentAction.mqttSubscribeActionData.mqttSubscribeActions.forEach((action) => {
          if (action.id && !processedIds.has(action.id)) {
            queue.push(action);
          }
        });
      }
    }

    return result;
  };

  getProjectCompatibleActionDefinitions = (): IActionDefinitionDto[] => {
    const platform: AttachablePlatformContext = {
      projectType: this.props.sd.project.projectType,
      isWebScreen: this.props.sd.getIsWebScreen(),
      dashboardState: this.props.sd.dashboardState,
      isOfflineScreen: this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl()
    };
    return this.props.sd.actionDefinitions.filter((def) => isActionDefinitionPlatformCompatible(def, platform));
  };

  generateActionResultItems = (
    targetType: string,
    targetCardinality: ECardinality,
    isMaster?: boolean
  ): CascaderOptionType => {
    const actionDefinitions: IActionDefinitionDto[] = this.getProjectCompatibleActionDefinitions();
    let actions: IAction[] = this.getAllActions(isMaster);
    /* let actions: IAction[] =
      this.props.isConditionPicker && this.props.sd.dashboardState === DashboardState.workFlow
        ? this.getWorkflowActionsFromOtherSteps()
        : this.getAllActions(isMaster); */

    if (this.props.action) {
      actions = actions.filter((x) => x.id !== this.props.action?.id);
    }

    const result: CascaderOptionType = {
      category: ESymbolCategory.actionResults,
      label: "Action Results",
      children: []
    };

    const parentParam = this.getParentParameter();
    const isParentList = parentParam?.cardinality === ECardinality.Many;

    for (const action of actions) {
      const definition = this.getActionDefinition(action, actionDefinitions);
      if (!definition || !definition.returnTypeName) {
        continue;
      }

      if (this.shouldSkipActionForObject(definition, parentParam, action)) {
        continue;
      }

      const itemAction = this.createActionItem(definition, action);

      if (this.props.isObject) {
        itemAction.converterOfCardinality = ECardinalityConversion.self;
      }

      this.processActionItem(itemAction, definition, targetType, targetCardinality, parentParam, isParentList, result);
    }

    // Alphabetical: the incoming order is just the order they sit in on the screen's events. Before
    // the loading placeholder is appended, so that stays pinned to the tail (KOA-18235).
    result.children?.sort((first: CascaderOptionType, second: CascaderOptionType): number =>
      getDesignerSymbolPickerOptionName(first).localeCompare(getDesignerSymbolPickerOptionName(second), undefined, {
        sensitivity: "base"
      })
    );

    if (this.props.sd.isDynamicTypesLoading) {
      const loadingItem = createActionResultsLoadingItem();
      if (result.children) {
        result.children.push(loadingItem);
      } else {
        result.children = [loadingItem];
      }
    }

    return result;
  };

  private getWorkflowActionsFromOtherSteps(): IAction[] {
    try {
      const wf = this.props.sd?.selectedWf;
      const current = this.props.sd?.getSelectedWfStep?.();
      if (!wf?.nodes) return [];

      const collected: IAction[] = [];

      const visit = (node: IWFNodes) => {
        const steps = node.steps ?? [];
        for (const s of steps) {
          if (!current || s?.id !== current.id) {
            if (Array.isArray(s?.actions)) {
              collected.push(...s.actions);
            }
          }
          if (s?.subprocess) visit(s.subprocess);
        }
      };

      visit(wf.nodes);

      const nested: IAction[] = [];
      for (const a of collected) {
        nested.push(...this.getNestedActions(a));
      }

      return _.uniqWith([...collected, ...nested], (a, b) => a.label === b.label && a.tag === b.tag);
    } catch {
      return [];
    }
  }

  private getParentParameter(): IActionParameter | undefined {
    return this.props.parameter?.ownerPropName !== undefined && this.props.action
      ? KMainFunctions.findParameter(this.props.action?.parameters, this.props.parameter.ownerPropName)
      : undefined;
  }

  private getActionDefinition(
    action: IAction,
    actionDefinitions: IActionDefinitionDto[]
  ): IActionDefinitionDto | undefined {
    let definition = actionDefinitions.find((x) => x.id === action.actionDefinitionId);

    if (!definition) {
      return undefined;
    }

    definition = _.cloneDeep(definition);

    if (action.label === "GetFromBag" || action.label === "FetchFromCache") {
      definition.returnTypeModel = action.returnTypeModel as IKType;
      definition.returnTypeName = action.returnType as string;
      definition.returnCardinality = action.returnCardinality as ECardinality;
    }

    if (!definition.returnTypeModel) {
      definition.returnTypeModel = { name: definition.returnTypeName } as IKType;
    }

    return definition;
  }

  private shouldSkipActionForObject(
    definition: IActionDefinitionDto,
    parentParam: IActionParameter | undefined,
    action: IAction
  ): boolean {
    return (
      (this.props.isObject &&
        definition.returnCardinality !== ECardinality.Many &&
        this.props.targetCardinality === ECardinality.Many &&
        parentParam?.symbol.actionDefinitionId !== action.actionDefinitionId) ??
      false
    );
  }

  private createActionItem(definition: IActionDefinitionDto, action: IAction): CascaderOptionType {
    const label = KMainFunctions.getFormattedSystemActionName(definition);
    return this.generateItemLabel(
      label,
      definition.returnTypeName,
      definition.returnCardinality,
      ESymbolCategory.actionResult,
      definition,
      undefined,
      { actionId: action.id, tag: action.tag }
    );
  }

  private processActionItem(
    itemAction: CascaderOptionType,
    definition: IActionDefinitionDto,
    targetType: string,
    targetCardinality: ECardinality,
    parentParam: IActionParameter | undefined,
    isParentList: boolean,
    result: CascaderOptionType
  ): void {
    if (targetType === "Any") {
      const directAction = { ...itemAction };
      delete directAction.children;
      result.children?.push(directAction);

      if (definition.returnTypeModel.elements && definition.returnTypeModel.elements.length > 0) {
        this.addActionChildren(itemAction, definition, targetType, targetCardinality, parentParam, isParentList);
        this.addActionIfUnique(itemAction, result);
      }
      return;
    }

    const compatibility = this.checkActionCompatibility(definition, targetType, targetCardinality);

    if (this.shouldAddDirectly(compatibility, targetType)) {
      result.children?.push(itemAction);
      return;
    }

    if (this.shouldAddAsObject(compatibility, targetType)) {
      this.addAsObjectIfUnique(itemAction, result);
    }

    this.addActionChildren(itemAction, definition, targetType, targetCardinality, parentParam, isParentList);

    if (itemAction.children && itemAction.children.length > 0) {
      this.addActionIfUnique(itemAction, result);
    }
  }

  private checkActionCompatibility(
    definition: IActionDefinitionDto,
    targetType: string,
    targetCardinality: ECardinality
  ): {
    isTypesCompatible: boolean;
    isCardinalityCompatible: boolean;
    isActionResultObject: boolean;
    hasNoReturnType: boolean;
  } {
    const isTypesCompatible =
      (targetType !== "Unknown" &&
        targetType !== "Any" &&
        ScreenDesignHelper.getAreTypesCompatible(definition.returnTypeModel.name, targetType)) ||
      targetType === "Object";

    const isCardinalityCompatible =
      this.checkCardinality(definition.returnCardinality, targetCardinality) || targetType === "Object";

    const isActionResultObject =
      definition.returnTypeModel.elements !== undefined && definition.returnTypeModel.elements.length > 0;

    const hasNoReturnType = !definition.returnTypeModel.elements || definition.returnTypeModel.elements.length === 0;

    return {
      isTypesCompatible,
      isCardinalityCompatible,
      isActionResultObject,
      hasNoReturnType
    };
  }

  private shouldAddDirectly(
    compatibility: {
      isTypesCompatible: boolean;
      isCardinalityCompatible: boolean;
      isActionResultObject: boolean;
      hasNoReturnType: boolean;
    },
    targetType: string
  ): boolean {
    return (
      !this.props.isObject &&
      ((compatibility.isTypesCompatible && compatibility.isCardinalityCompatible) ||
        (targetType === "Unknown" && compatibility.hasNoReturnType))
    );
  }

  private shouldAddAsObject(
    compatibility: {
      isTypesCompatible: boolean;
      isCardinalityCompatible: boolean;
      isActionResultObject: boolean;
      hasNoReturnType: boolean;
    },
    targetType: string
  ): boolean {
    if (this.props.isConditionPicker && compatibility.isActionResultObject) {
      return false;
    }

    return (
      compatibility.isCardinalityCompatible &&
      compatibility.isActionResultObject &&
      (!knownTypes.includes(targetType.toLowerCase()) || targetType === "Any")
    );
  }

  private addAsObjectIfUnique(itemAction: CascaderOptionType, result: CascaderOptionType): void {
    const itemActionAsObject = { ...itemAction };
    if (itemActionAsObject.children?.length === 0) {
      delete itemActionAsObject.children;
    }

    if (!this.isActionAlreadyAdded(itemActionAsObject, result)) {
      result.children?.push(itemActionAsObject);
    }
  }

  private addActionChildren(
    itemAction: CascaderOptionType,
    definition: IActionDefinitionDto,
    targetType: string,
    targetCardinality: ECardinality,
    parentParam: IActionParameter | undefined,
    isParentList: boolean
  ): void {
    if (parentParam) {
      if (isParentList && parentParam.symbol.actionDefinitionId === definition.id) {
        itemAction.children = this.generateFieldsOfType(
          definition.returnTypeModel,
          targetType,
          undefined,
          undefined,
          this.props.targetCardinality
        );
      } else {
        itemAction.children = this.generateCardinalityConversion(
          definition.returnTypeModel,
          definition.returnCardinality,
          targetType,
          targetCardinality,
          itemAction.actionDefinition,
          undefined,
          false,
          parentParam
        );
      }
    } else if (
      definition.returnCardinality === ECardinality.One &&
      (targetCardinality === ECardinality.One || targetType === "Any")
    ) {
      itemAction.children = this.generateFieldsOfType(
        definition.returnTypeModel,
        targetType,
        undefined,
        undefined,
        this.props.targetCardinality
      );
    } else {
      itemAction.children = this.generateCardinalityConversion(
        definition.returnTypeModel,
        definition.returnCardinality,
        targetType,
        targetCardinality,
        itemAction.actionDefinition
      );
    }
  }

  private addActionIfUnique(itemAction: CascaderOptionType, result: CascaderOptionType): void {
    if (!this.isActionAlreadyAdded(itemAction, result)) {
      result.children?.push(itemAction);
    }
  }

  private isActionAlreadyAdded(itemAction: CascaderOptionType, result: CascaderOptionType): boolean {
    if (!result.children) {
      return false;
    }
    return result.children.some(
      (x: CascaderOptionType) =>
        this.getActionLabelKey(x) === this.getActionLabelKey(itemAction) &&
        ((x.children && itemAction.children) || (!x.children && !itemAction.children)) &&
        x.resultActionId === itemAction.resultActionId
    );
  }

  private getActionLabelKey(action: CascaderOptionType): string {
    const label = action.label;
    if (!React.isValidElement(label)) {
      return "";
    }
    if (label.key !== undefined && label.key !== null) {
      return String(label.key);
    }
    const childProps = (label.props as { children?: { key?: React.Key | null } } | undefined)?.children;
    if (childProps && childProps.key !== undefined && childProps.key !== null) {
      return String(childProps.key);
    }
    return "";
  }

  checkCardinality = (cardinality: ECardinality, targetCardinality: ECardinality): boolean => {
    if (cardinality === ECardinality.Many && targetCardinality === ECardinality.Many) {
      return true;
    }

    if (cardinality === ECardinality.One && targetCardinality === ECardinality.One) {
      return true;
    }

    if (cardinality === ECardinality.Any || targetCardinality === ECardinality.Any) {
      return true;
    }

    return false;
  };

  generateDefaultItems = (targetType: string): CascaderOptionType => {
    const children: CascaderOptionType[] = [
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.DateNow,
        label: "Now",
        toType: "DateTimeOffset"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.Empty,
        label: "Empty",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.Space,
        label: "Space",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.True,
        label: "True",
        toType: "Boolean"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.False,
        label: "False",
        toType: "Boolean"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.NewGuid,
        label: "New Guid",
        toType: "Guid"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.EmptyGuid,
        label: "Empty Guid",
        toType: "Guid"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.ClientIpAddress,
        label: "Client IP Address",
        toType: "String"
      }
      // {
      //   category: ESymbolCategory.default,
      //   defaultValueType: EDefaultValueType.GetProcessInitiator,
      //   label: "Process Initiator",
      //   toType: "String",
      //   inputName: "ProcessInitiator"
      // },
      // { category: ESymbolCategory.default, defaultValueType: EDefaultValueType.GetCurrentLocation, label: "Current Location", toType: "Location" },
    ];

    if (
      this.props.sd.project.projectType === EProjectType.Desktop &&
      this.props.sd.dashboardState !== DashboardState.workFlow
    ) {
      children.push({
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentURL,
        label: "Current URL",
        toType: "String"
      });
    }

    if (this.props.sd.dashboardState === DashboardState.design) {
      children.push({
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.IsMicroApp,
        label: "Is MicroApp",
        toType: "Boolean"
      });
    }
    if (!this.props.isGenericSelect) {
      children.push({
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.GetVersionInfo,
        label: "Version Info",
        toType: "String"
      });
    }
    if (targetType === "Unknown") {
      children.push({
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.Null,
        label: "null",
        toType: "String"
      });
    } else {
      children.push(
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "DateTimeOffset"
        },
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "Date"
        },
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "DateTime"
        },
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "String"
        },
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "Boolean"
        },
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "Double"
        },
        {
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: "Guid"
        }
      );
      if (this.props.parameter?.isObject && this.props.parameter?.cardinality !== ECardinality.Many) {
        children.push({
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.CustomBinding,
          label: "Custom Binding",
          toType: "Unknown"
        });
        children.push({
          category: ESymbolCategory.default,
          defaultValueType: EDefaultValueType.Null,
          label: "null",
          toType: targetType
        });
      }
    }

    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    if (this.props.sd.dashboardState === DashboardState.reportDesigner && selectedDi?.item?.name === "Label") {
      children.push({
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentPageNumber,
        label: "Current Page Number",
        toType: "String"
      });
      children.push({
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.TotalPageNumber,
        label: "Total Page Number",
        toType: "String"
      });
    }
    const currentUserChildren: CascaderOptionType[] = [
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.GetMyUsername,
        label: "Current Username",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentUserid,
        label: "Current Userid",
        toType: "Guid"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentUserFirstName,
        label: "Current First Name",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentUserLastName,
        label: "Current Last Name",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentUserFullName,
        label: "Current Full Name",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentUserPhoneNumber,
        label: "Current Phone Number",
        toType: "String"
      },
      {
        category: ESymbolCategory.default,
        defaultValueType: EDefaultValueType.CurrentUserRoleNames,
        label: "Current Role Names",
        toType: "String"
      }
    ];

    let effectiveCurrentUserChildren = currentUserChildren;

    // KOA-18679: no signed-in user to read here — reports take user data through Report Inputs.
    if (
      this.props.sd.dashboardState === DashboardState.workFlow ||
      this.props.sd.dashboardState === DashboardState.reportDesigner ||
      this.props.sd.dashboardState === DashboardState.scheduledJobs
    ) {
      effectiveCurrentUserChildren = [];
    }

    if (effectiveCurrentUserChildren.length > 0) {
      const currentUserGroup: CascaderOptionType = {
        category: ESymbolCategory.default,
        label: "User ",
        children: effectiveCurrentUserChildren
      };

      children.push(currentUserGroup);
    }

    // For filtering children
    const filtered: CascaderOptionType[] = [];

    for (const node of children) {
      const isGroup = Array.isArray(node.children) && node.children.length > 0;

      if (!isGroup) {
        if (node.toType && ScreenDesignHelper.getAreTypesCompatible(node.toType, targetType)) {
          filtered.push(node);
        }
      } else {
        const keptChildren = (node.children || []).filter(
          (c) => c.toType && ScreenDesignHelper.getAreTypesCompatible(c.toType, targetType)
        );

        if (keptChildren.length > 0) {
          filtered.push({ ...node, children: keptChildren });
        }
      }
    }

    const defaultOption = {
      label: "Default",
      children: filtered
    } as CascaderOptionType;

    if (defaultOption.children && defaultOption.children.filter((x) => x.label === "null").length > 1) {
      defaultOption.children = defaultOption.children.filter((x) => {
        if (x.label !== "null") return true;
        return x.toType === targetType;
      });
    }

    return defaultOption;
  };

  generateWorkFlowBagItems = (targetType: string, targetCardinality: ECardinality): CascaderOptionType => {
    let wf = this.props.sd.dashboardState === DashboardState.workFlow ? this.props.sd.selectedWf : undefined;
    const children: CascaderOptionType[] = [];
    const processStepScreen = this.props.sd.project.processStepScreenIds?.find(
      (p) => p.screenId === this.props.sd.sdId
    );
    if (processStepScreen && this.props.sd.dashboardState === DashboardState.design) {
      const project = this.props.sd.projectsWithWFData.find((p) => p.id === processStepScreen.projectId);
      const process = project?.workflows.find((w) => w.id == processStepScreen.processId); // this.props.sd.workFlowList?.find((p) => p.id === processStepScreen.processId);
      if (process) {
        wf = process;
      }
    }
    if (
      wf &&
      (this.props.sd.dashboardState === DashboardState.design ||
        this.props.sd.dashboardState === DashboardState.workFlow)
    ) {
      const businessObjectCategory: CascaderOptionType = {
        category: ESymbolCategory.workFlowBOParameters,
        label: "Business Object",
        children: []
      };
      wf.parameters
        ?.filter((param) => param.isBusinessObject)
        .forEach((wfBagParameter) => {
          const itemInput: CascaderOptionType = {
            label: wfBagParameter.name,
            category: ESymbolCategory.processParameter,
            inputName: wfBagParameter.id,
            domainType: wfBagParameter.dataType
          };
          itemInput.children = this.generateCardinalityConversion(
            toKType(String(itemInput.domainType!)),
            ECardinality.One,
            targetType,
            targetCardinality
          );
          businessObjectCategory.children?.push(itemInput);
        });
      const internalCategory: CascaderOptionType = {
        category: ESymbolCategory.workFlowInternalParameters,
        label: "Internal",
        children: []
      };
      wf.parameters
        ?.filter((param) => !param.isBusinessObject)
        .forEach((wfBagParameter) => {
          const itemInput: CascaderOptionType = {
            label: wfBagParameter.name,
            category: ESymbolCategory.processParameter,
            inputName: wfBagParameter.id,
            domainType: wfBagParameter.dataType
          };

          itemInput.children = this.generateCardinalityConversion(
            toKType(String(itemInput.domainType!)),
            ECardinality.One,
            targetType,
            targetCardinality
          );

          internalCategory.children?.push(itemInput);
        });

      if (businessObjectCategory.children && businessObjectCategory.children.length > 0)
        children.push(businessObjectCategory);

      if (internalCategory.children && internalCategory.children.length > 0) children.push(internalCategory);

      // VTO: Process common parameters
      const instanceCategory: CascaderOptionType = {
        category: ESymbolCategory.workFlowInstanceParameters,
        label: "Common",
        children: []
      };
      const wfInstanceId: CascaderOptionType = {
        label: "ProcessInstanceId",
        category: ESymbolCategory.processParameter,
        inputName: "processInstance",
        domainType: "String" as BackEndValueType
      };
      wfInstanceId.children = this.generateCardinalityConversion(
        toKType(String(wfInstanceId.domainType!)),
        ECardinality.One,
        targetType,
        targetCardinality
      );
      instanceCategory.children?.push(wfInstanceId);

      if (!this.props.isConditionPicker) {
        const stepInstanceId: CascaderOptionType = {
          label: "StepInstanceId",
          category: ESymbolCategory.processParameter,
          inputName: "workflowStepInstanceId",
          domainType: "String" as BackEndValueType
        };
        stepInstanceId.children = this.generateCardinalityConversion(
          toKType(String(stepInstanceId.domainType!)),
          ECardinality.One,
          targetType,
          targetCardinality
        );
        instanceCategory.children?.push(stepInstanceId);
      }

      // "Process Initiator Email/Username" resolves to the initiator's username (= e-mail);
      // "Process Initiator FullName" resolves to the full name.
      const processInitiator: CascaderOptionType = {
        label: "Process Initiator Email/Username",
        category: ESymbolCategory.processParameter,
        inputName: "processInitiator",
        domainType: "String" as BackEndValueType
      };
      processInitiator.children = this.generateCardinalityConversion(
        toKType(String(processInitiator.domainType!)),
        ECardinality.One,
        targetType,
        targetCardinality
      );
      instanceCategory.children?.push(processInitiator);

      const processInitiatorFullName: CascaderOptionType = {
        label: "Process Initiator FullName",
        category: ESymbolCategory.processParameter,
        inputName: "processInitiatorFullName",
        domainType: "String" as BackEndValueType
      };
      processInitiatorFullName.children = this.generateCardinalityConversion(
        toKType(String(processInitiatorFullName.domainType!)),
        ECardinality.One,
        targetType,
        targetCardinality
      );
      instanceCategory.children?.push(processInitiatorFullName);

      const selStep = this.props.sd.getSelectedWfStep();
      const miEnabled = !!(selStep?.id && getBpmnMiState(selStep.id).enabled);

      if (miEnabled) {
        if (!this.props.hideMiInstanceCountParam) {
          const miInstanceCount: CascaderOptionType = {
            label: "MI Instance Count",
            category: ESymbolCategory.processParameter,
            inputName: MI_INSTANCE_COUNT_KEY,
            domainType: "Int32" as BackEndValueType
          };
          miInstanceCount.children = this.generateCardinalityConversion(
            toKType(String(miInstanceCount.domainType!)),
            ECardinality.One,
            targetType,
            targetCardinality
          );
          instanceCategory.children?.push(miInstanceCount);
          const miCompleted: CascaderOptionType = {
            label: "MI Completed Count",
            category: ESymbolCategory.processParameter,
            inputName: MI_COMPLETED_COUNT_KEY,
            domainType: "Int32" as BackEndValueType
          };
          miCompleted.children = this.generateCardinalityConversion(
            toKType(String(miCompleted.domainType!)),
            ECardinality.One,
            targetType,
            targetCardinality
          );
          instanceCategory.children?.push(miCompleted);
        }
      }

      const loopEnabled = !!(selStep?.id && getBpmnLoopState(selStep.id).enabled);
      if (loopEnabled) {
        const loopIter: CascaderOptionType = {
          label: "Loop Iteration",
          category: ESymbolCategory.processParameter,
          inputName: LOOP_ITERATION_KEY,
          domainType: "Int32" as BackEndValueType
        };
        loopIter.children = this.generateCardinalityConversion(
          toKType(String(loopIter.domainType!)),
          ECardinality.One,
          targetType,
          targetCardinality
        );
        instanceCategory.children?.push(loopIter);
      }

      const step = this.props.sd.getSelectedWfStep();
      // VTO: Currently only available on Screen Steps
      if (
        this.props.sd.dashboardState === DashboardState.workFlow &&
        step &&
        step.stepType === EWFStepType.ScreenInteraction
      ) {
        const stepAssignedUser: CascaderOptionType = {
          label: "Assigned User",
          category: ESymbolCategory.processParameter,
          inputName: "stepAssignedUser",
          domainType: "String" as BackEndValueType
        };
        stepAssignedUser.children = this.generateCardinalityConversion(
          toKType(String(stepAssignedUser.domainType!)),
          ECardinality.One,
          targetType,
          targetCardinality
        );
        instanceCategory.children?.push(stepAssignedUser);

        const stepTaskLink: CascaderOptionType = {
          label: "Task Link",
          category: ESymbolCategory.processParameter,
          inputName: "stepTaskLink",
          domainType: "String" as BackEndValueType
        };
        stepTaskLink.children = this.generateCardinalityConversion(
          toKType(String(stepTaskLink.domainType!)),
          ECardinality.One,
          targetType,
          targetCardinality
        );
        instanceCategory.children?.push(stepTaskLink);

        this.props.sd.projectsWithExternalWFData.forEach((p) => {
          if (p.processScreenInfo.some((si) => si.stepId === step.id)) {
            const dynamicParameterName = `Task Link for "${p.name}"`;
            const dynamicParameterInput = `stepTaskLink_${p.id}`;

            const stepTaskLink: CascaderOptionType = {
              label: dynamicParameterName,
              category: ESymbolCategory.processParameter,
              inputName: dynamicParameterInput,
              domainType: "String" as BackEndValueType
            };
            stepTaskLink.children = this.generateCardinalityConversion(
              toKType(String(stepTaskLink.domainType!)),
              ECardinality.One,
              targetType,
              targetCardinality
            );
            instanceCategory.children?.push(stepTaskLink);
          }
        });

        if (step.userInteractions && step.userInteractions.length > 0) {
          step.userInteractions.forEach((ui) => {
            // A mail action link completes the step with NO form, so an interaction whose Business
            // Object outputs carry no value would write the BO back empty. Its link is listed but
            // DISABLED — a silently missing entry read as a bug (mirrors the "Add User Interactions
            // to Mail" multiselect gating). The label stays clean; the reason rides on the row's
            // native hover tooltip (rc-cascader renders `option.title` as the li's title).
            const notifiable: boolean = isInteractionNotifiable(this.props.sd.selectedWf, step, ui);
            const userInteractionTaskLink: CascaderOptionType = {
              label: `"${ui.label}" Mail Action Link`,
              category: ESymbolCategory.processParameter,
              inputName: `mailInteractionLink_${ui.label}`,
              domainType: "String" as BackEndValueType,
              disabled: !notifiable
            };
            if (!notifiable) {
              userInteractionTaskLink.title =
                "Needs Business Object values — set them in the Forms tab's Interactions editor.";
            }
            if (notifiable) {
              userInteractionTaskLink.children = this.generateCardinalityConversion(
                toKType(String(userInteractionTaskLink.domainType!)),
                ECardinality.One,
                targetType,
                targetCardinality
              );
            }
            instanceCategory.children?.push(userInteractionTaskLink);
          });
        }

        // this actually has to be list of users, list of objects is not useful in designtime, therefore returns csv (usernames)
        const stepClaimableUsers: CascaderOptionType = {
          label: "Claimable Users",
          category: ESymbolCategory.processParameter,
          inputName: "stepClaimableUsers",
          domainType: "String" as BackEndValueType
        };
        stepClaimableUsers.children = this.generateCardinalityConversion(
          toKType(String(stepClaimableUsers.domainType!)),
          ECardinality.One,
          targetType,
          targetCardinality
        );
        instanceCategory.children?.push(stepClaimableUsers);
      }

      children.push(instanceCategory);
    }
    return {
      category: ESymbolCategory.processParameter,
      label: "Process Parameters",
      children
    } as CascaderOptionType;
  };

  generateRoleList = (): CascaderOptionType => {
    let children: CascaderOptionType[] = [];
    const roleList = this.props.sd.project?.roles ?? [];

    for (let j = 0; j < roleList?.length; j++) {
      const role = roleList[j];
      const itemDi = {
        category: ESymbolCategory.roleList,
        label: role.name
      } as CascaderOptionType;

      children.push(itemDi);
    }

    if (children) {
      children = children.sort((a, b) => {
        if (a.label && b.label && a.label < b.label) return -1;
        if (a.label && b.label && a.label > b.label) return 1;
        return 0;
      });
    }
    return {
      category: ESymbolCategory.roleList,
      label: "Role List",
      children
    } as CascaderOptionType;
  };

  /**
   * Converts an IMethodInput (with nested parameters) into an IKType structure
   * so that existing generateCardinalityConversion / generateFieldsOfType can work with it.
   */
  private methodInputToKType = (input: IMethodInput): IKType => {
    const elements: IKTypeField[] = [];
    if (input.parameters && input.parameters.length > 0) {
      for (const child of input.parameters) {
        // type must be a raw string (not IKType object) because generateFieldsOfType
        // calls .toString() on it and getAreTypesCompatible expects a string.
        const childTypeName = child.type || "String";
        const isList = child.cardinality === ECardinality.Many;
        let elementTypeModel: IKType | undefined;

        if (child.isObject && child.parameters && child.parameters.length > 0) {
          elementTypeModel = this.methodInputToKType(child);
        }

        elements.push({
          // IKTypeField.type is typed IKType but is a type-name string at runtime (see builder.dtos.ts).
          // generateFieldsOfType calls .toString() on it and getAreTypesCompatible does string ops, so a
          // toKType(...) object here would surface as "[object Object]" and drop the field.
          name: child.name,
          type: childTypeName as unknown as IKType,
          isList,
          elementTypeModel
        });
      }
    }
    return {
      id: 0,
      name: input.objectClassName || input.type || input.name,
      elements
    };
  };

  /**
   * Builds cascader children for a single method input, handling cardinality and nested object fields.
   * For primitive types with cardinality One, preserves the original behavior (passes raw type string).
   * Only uses IKType conversion for object types (nested fields) and Many cardinality (First/Last).
   */
  private buildMethodInputChildren = (
    input: IMethodInput,
    targetType: string,
    targetCardinality: ECardinality
  ): CascaderOptionType[] => {
    const inputCardinality = input.cardinality ?? ECardinality.One;

    // If the input is an object with nested parameters, convert to IKType for field access
    if (input.isObject && input.parameters && input.parameters.length > 0) {
      const kType = this.methodInputToKType(input);
      return this.generateCardinalityConversion(kType, inputCardinality, targetType, targetCardinality);
    }

    // For Many cardinality on primitive types, use IKType to enable First/Last options
    if (inputCardinality === ECardinality.Many) {
      const simpleKType: IKType = { id: 0, name: String(input.type || "String") };
      return this.generateCardinalityConversion(simpleKType, inputCardinality, targetType, targetCardinality);
    }

    // Primitive type with One cardinality: what travels here is the type NAME — the conversion
    // table and getAreTypesCompatible match on it as a string. Both primitive branches coerce,
    // like every other toKType call site: a non-string (a legacy numeric EDataType on an
    // unrepaired record) reaches `.includes()` and throws, taking the whole picker down rather
    // than one option.
    return this.generateCardinalityConversion(
      toKType(String(input.type || "String")),
      inputCardinality,
      targetType,
      targetCardinality
    );
  };

  /**
   * The whole option set for the Document Parameters tool (KOA-17254).
   *
   * That surface has no screen, so almost every symbol category is unresolvable there — components, screen inputs,
   * role lists and the rest would only be bindings that cannot be evaluated. Exactly two things can: a declared
   * document input (supplied per resolve call at the call site) and a source action's result field.
   *
   * One method serves both pickers on the surface, because both ask the same question. A parameter row asks "where
   * does this tag's value come from"; a source action's parameter asks "where does this input come from". Answer in
   * both cases: an input, or another action's field — the latter being the chaining case. The fixed-value option
   * follows the caller's `hideFixedValue`, which is how the tag rows exclude it while action parameters keep it.
   */
  generateDocumentParameterItems = (targetType: string, targetCardinality: ECardinality): CascaderOptionType[] => {
    const items: CascaderOptionType[] = [];

    if (this.props.hideFixedValue !== true && knownTypes.includes(targetType.toLowerCase())) {
      items.push({ category: ESymbolCategory.fixed, label: "Fixed Value", isFixed: true } as CascaderOptionType);
    }

    const inputsOption: CascaderOptionType = this.generateDocumentParameterInputItems(targetType, targetCardinality);
    if (inputsOption.children && inputsOption.children.length > 0) {
      items.push(inputsOption);
    }

    if (this.props.hideActionResults !== true) {
      const actionResultsOption: CascaderOptionType = this.generateActionResultItems(targetType, targetCardinality);
      actionResultsOption.children = actionResultsOption.children?.filter((x) => x.label);
      if (actionResultsOption.children && actionResultsOption.children.length > 0) {
        items.push(actionResultsOption);
      }
    }

    items.forEach((element) => this.setKeyOfNodeTree(element));
    return items;
  };

  /**
   * The declared document inputs, shaped exactly like screen inputs — same leaf category convention, same
   * cardinality conversion children — because they play the same role one level up: a value the caller supplies.
   */
  generateDocumentParameterInputItems = (targetType: string, targetCardinality: ECardinality): CascaderOptionType => {
    const inputs: IDocumentParameterInput[] = this.props.sd.documentParameterInputs ?? [];
    const children: CascaderOptionType[] = inputs
      .filter((input: IDocumentParameterInput) => !!input?.name)
      .map((input: IDocumentParameterInput) => {
        const itemInput: CascaderOptionType = {
          label: input.name,
          category: ESymbolCategory.documentParameterInput,
          inputName: input.name,
          domainType: input.typeName
        };

        itemInput.children = this.generateCardinalityConversion(
          itemInput.domainType!,
          input.cardinality ?? ECardinality.One,
          targetType,
          targetCardinality
        );
        return itemInput;
      });

    return {
      category: ESymbolCategory.documentParameterInputs,
      label: "Document Inputs",
      children
    } as CascaderOptionType;
  };

  generateScreenInputItems = (
    targetType: string,
    targetCardinality: ECardinality,
    isMaster?: boolean
  ): CascaderOptionType => {
    const screenInputs = isMaster
      ? this.props.sd.getMasterScreenInputsByScreenId(this.props.sd.sdId)
      : this.props.sd.getScreenInputs();
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();

    if (
      (api && apiMethod && this.props.sd.dashboardState === DashboardState.apiManagement) ||
      (ds?.datasourceType === EDatasourceType.BleService && bleMethod)
    ) {
      const children: CascaderOptionType[] = [];
      // A List of Objects method is offered as its `Items` wrapper, NOT unwrapped like the input
      // panel lists it: the wrapper is the only input the backend knows, so a binding has to read
      // a field through it (Items → First/Last → field) to validate and compile.
      const inputs = apiMethod?.inputMethods || bleMethod?.bluetoothInfo?.inputs || [];

      inputs.forEach((input: IMethodInput) => {
        const itemInput: CascaderOptionType = {
          label: input.name,
          category: ESymbolCategory.inputMethod,
          inputName: input.name,
          domainType: input.type
        };

        itemInput.children = this.buildMethodInputChildren(input, targetType, targetCardinality);
        children.push(itemInput);
      });
      return {
        category: ESymbolCategory.inputMethod,
        label: "Method Inputs",
        children
      } as CascaderOptionType;
    }
    const children: CascaderOptionType[] = [];
    if (screenInputs) {
      screenInputs.forEach((input) => {
        const itemInput: CascaderOptionType = {
          label: input.name,
          category: ESymbolCategory.screenInput,
          inputName: input.name,
          domainType: input.type
        };

        itemInput.children = this.generateCardinalityConversion(
          itemInput.domainType!,
          ECardinality.One,
          targetType,
          targetCardinality
        );
        children.push(itemInput);
      });
    }
    return {
      category: ESymbolCategory.screenInputs,
      label: this.props.sd.dashboardState === DashboardState.reportDesigner ? "Report Inputs" : "Screen Inputs",
      children
    } as CascaderOptionType;
  };

  generateScreenOutputItems = (targetType: string, targetCardinality: ECardinality): CascaderOptionType => {
    const screenOutputs = this.props.sd.getScreenOutputs();
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();

    if (
      (api && apiMethod && this.props.sd.dashboardState === DashboardState.apiManagement) ||
      (ds?.datasourceType === EDatasourceType.BleService && bleMethod)
    ) {
      const children: CascaderOptionType[] = [];
      // Offered as the `Items` wrapper for List of Objects — see generateScreenInputItems.
      const inputs = apiMethod?.inputMethods || bleMethod?.bluetoothInfo?.inputs || [];
      inputs.forEach((input: IMethodInput) => {
        const itemInput: CascaderOptionType = {
          label: input.name,
          category: ESymbolCategory.inputMethod,
          inputName: input.name,
          domainType: input.type
        };

        itemInput.children = this.buildMethodInputChildren(input, targetType, targetCardinality);
        children.push(itemInput);
      });
      return {
        category: ESymbolCategory.inputMethod,
        label: "Method Inputs",
        children
      } as CascaderOptionType;
    }
    const children: CascaderOptionType[] = [];
    if (screenOutputs) {
      screenOutputs.forEach((input) => {
        const itemInput: CascaderOptionType = {
          label: input.name,
          category: ESymbolCategory.screenInput,
          inputName: input.name,
          domainType: input.type
        };

        itemInput.children = this.generateCardinalityConversion(
          itemInput.domainType!,
          ECardinality.One,
          targetType,
          targetCardinality
        );
        children.push(itemInput);
      });
    }
    return {
      // TODO: new "screenOutputs" & "screenOutput" categories should be added ??
      category: ESymbolCategory.screenInputs,
      label: "Screen Outputs",
      children
    } as CascaderOptionType;
  };

  generateBagItems = (_targetType: string, _targetCardinality: ECardinality): CascaderOptionType => {
    const bagsItem: CascaderOptionType = {
      category: ESymbolCategory.bags,
      label: "Bags",
      children: []
    };
    return bagsItem;
  };

  getKanbanCurrentRows = (
    di: IDisplayItem,
    kanbanBoardDI: IDisplayItem,
    databindingOptions: IDisplayItemProperty[],
    targetTypeName: string
  ) => {
    const currentLaneNode: CascaderOptionType = {
      label: "Current Lane",
      category: ESymbolCategory.current,
      diId: di.id,
      isKanbanLanePropSelected: true
    };
    const currentCardNode: CascaderOptionType = {
      label: "Current Card",
      category: ESymbolCategory.current,
      diId: di.id,
      isKanbanCardPropSelected: true
    };
    const laneDatabindingOptions = databindingOptions.find((p) => p.name === "laneOptions");
    const cardDatabindingOptions = databindingOptions.find((p) => p.name === "cardOptions");

    if (laneDatabindingOptions) {
      currentLaneNode.children = this.generateFieldsOfType(
        this.calculateValueTypeByDi(kanbanBoardDI, laneDatabindingOptions),
        targetTypeName,
        undefined,
        undefined,
        this.props.targetCardinality
      );
    }

    if (cardDatabindingOptions) {
      currentCardNode.children = this.generateFieldsOfType(
        this.calculateValueTypeByDi(kanbanBoardDI, cardDatabindingOptions),
        targetTypeName,
        undefined,
        undefined,
        this.props.targetCardinality
      );
    }

    return [currentLaneNode, currentCardNode];
  };

  generateForEachCurrentRow = (di: IDisplayItem, targetTypeName: string, _targetCardinality: ECardinality) => {
    if (!this.props.action) return null as unknown as CascaderOptionType;

    const diEvents = di.item.events;
    let parentForEachActions: IAction[] = [];
    if (diEvents?.length) {
      for (let x = 0; x < diEvents.length; x++) {
        const event = diEvents[x];

        const actions = ScreenDesignHelper.fetchPredecessorForEachActionsOfAction(this.props.action.id, event.actions);

        if (actions.length > 0) {
          parentForEachActions = actions;
          break;
        }
      }

      const itemCurrentRow: CascaderOptionType = {
        label: "ForEach Current",
        category: ESymbolCategory.current,
        diId: di.id,
        isForEachNodeSelected: true
      };

      const findChildElementTypeByName = (type: IKType | undefined, returnTypeName: string): IKType | undefined => {
        const field = type?.elements?.find((el) => el.type.toString() === returnTypeName);
        if (field) {
          return field.elementTypeModel;
        }
        type?.elements?.forEach((element) => {
          if (element.elementTypeModel) {
            const childField = findChildElementTypeByName(element.elementTypeModel, returnTypeName);
            if (childField) {
              return childField;
            }
          }
        });
      };

      for (let action of parentForEachActions) {
        const actionDef = this.props.sd.actionDefinitions.find(
          (x) => x.id === action.forEachData?.symbolModel?.actionDefinitionId
        );

        if (actionDef) {
          // returnTypeModel can be null/undefined (e.g., when the action's return type isn't resolvable
          // at save-time) — guard every access and skip cleanly if we can't resolve a current row type.
          if (!actionDef.returnTypeModel) continue;
          const currentType =
            action.forEachData?.symbolModel?.targetType &&
            action.forEachData?.symbolModel?.targetType === actionDef.returnTypeModel.name
              ? actionDef.returnTypeModel
              : findChildElementTypeByName(
                  actionDef.returnTypeModel,
                  action.forEachData?.symbolModel?.targetType || ""
                );
          if (currentType === undefined) continue;
          itemCurrentRow.children = this.generateFieldsOfType(currentType, targetTypeName)?.map((option) => ({
            ...option,
            parentActionId: action.id
          }));
        }
      }

      return itemCurrentRow;
    }
  };

  generateCurrentRow = (
    di: IDisplayItem,
    targetTypeName: string,
    _targetCardinality: ECardinality
  ): CascaderOptionType => {
    const parents = this.getParents(di);

    let iteratorParents = parents.filter((x) => {
      return this.isDataBindingOptions(x);
    });

    if (di.item.name === "Calendar") {
      const itemCurrentRow: CascaderOptionType = {
        label: "Current",
        category: ESymbolCategory.current,
        diId: di.id
      };

      itemCurrentRow.children = [
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentDate",
          fieldtype: "k_currentDate",
          diPropName: "k_currentDate",
          field: {
            name: "k_currentDate"
          },
          children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentDay",
          fieldtype: "k_currentDay",
          diPropName: "k_currentDay",
          field: {
            name: "k_currentDay"
          },
          children: ScreenDesignHelper.generateTypeConversion("int16", targetTypeName)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentMonth",
          fieldtype: "k_currentMonth",
          diPropName: "k_currentMonth",
          field: {
            name: "k_currentMonth"
          },

          children: ScreenDesignHelper.generateTypeConversion("int16", targetTypeName)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentYear",
          fieldtype: "k_currentYear",
          diPropName: "k_currentYear",
          field: {
            name: "k_currentYear"
          },
          children: ScreenDesignHelper.generateTypeConversion("int16", targetTypeName)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentEvent",
          fieldtype: "k_currentEvent",
          diPropName: "k_currentEvent",
          field: {
            name: "k_currentEvent"
          },
          children: [
            {
              category: ESymbolCategory.fieldOfType,
              label: "id",
              fieldtype: "k_eventId",
              diPropName: "k_eventId",
              field: {
                name: "k_eventId"
              },
              children: ScreenDesignHelper.generateTypeConversion("guid", targetTypeName)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "title",
              fieldtype: "k_eventTitle",
              diPropName: "k_eventTitle",
              field: {
                name: "k_eventTitle"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "startDate",
              fieldtype: "k_eventStartDate",
              diPropName: "k_eventStartDate",
              field: {
                name: "k_eventStartDate"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "endDate",
              fieldtype: "k_eventEndDate",
              diPropName: "k_eventEndDate",
              field: {
                name: "k_eventEndDate"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
            },
            {
              category: ESymbolCategory.fieldOfType,
              label: "color",
              fieldtype: "k_eventColor",
              diPropName: "k_eventColor",
              field: {
                name: "k_eventColor"
              },
              children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
            }
          ]
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "panel",
          fieldtype: "k_panel",
          diPropName: "k_panel",
          field: {
            name: "k_panel"
          },
          children: ScreenDesignHelper.generateTypeConversion("string", targetTypeName)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentRangeStart",
          fieldtype: "k_currentRangeStart",
          diPropName: "k_currentRangeStart",
          field: {
            name: "k_currentRangeStart"
          },
          children: ScreenDesignHelper.generateTypeConversion("datetimeoffset", targetTypeName)
        },
        {
          category: ESymbolCategory.fieldOfType,
          label: "currentRangeEnd",
          fieldtype: "k_currentRangeEnd",
          diPropName: "k_currentRangeEnd",
          field: {
            name: "k_currentRangeEnd"
          },
          children: ScreenDesignHelper.generateTypeConversion("datetimeoffset", targetTypeName)
        }
      ];

      return itemCurrentRow;
    }

    const itemCurrentRow: CascaderOptionType = {
      label: "Current",
      category: ESymbolCategory.current,
      diId: di.id
    };

    if (iteratorParents.length > 0) {
      iteratorParents = this.sortDisplayItemsByHierarchy(iteratorParents);
      const iteratorParent = iteratorParents[iteratorParents.length - 1];
      const databindingOptions = this.getDataBindingOptions(iteratorParent);
      let currentType = this.calculateValueTypeByDi(iteratorParent, databindingOptions[0]);

      if (iteratorParent.item.name === "Maps" && databindingOptions[0].name === "waypoints") {
        currentType = this.calculateValueTypeByDi(iteratorParent, databindingOptions[1]);
      }

      if (iteratorParents.length > 1) {
        const iteratorsIteratorParent: IDisplayItem | undefined = iteratorParents[iteratorParents.length - 2];

        if (iteratorsIteratorParent && iteratorParent) {
          const databindingOptions: IDisplayItemProperty[] = this.getDataBindingOptions(iteratorsIteratorParent);

          if (databindingOptions.length > 0) {
            if (iteratorsIteratorParent.item.name === "KanbanBoard") {
              itemCurrentRow.children = this.getKanbanCurrentRows(
                di,
                iteratorsIteratorParent,
                databindingOptions,
                targetTypeName
              );

              return itemCurrentRow;
            }
            currentType = this.calculateValueTypeByDi(iteratorsIteratorParent, databindingOptions[0]);

            const iteratorParentDataBindingOptions: IDisplayItemProperty[] = this.getDataBindingOptions(iteratorParent);

            const returnType: IKTypeField | undefined = this.getReturnTypeModelByFieldName(
              currentType,
              iteratorParentDataBindingOptions[0].symbol.fieldName
            );

            if (returnType?.elementTypeModel) {
              currentType = returnType.elementTypeModel;
            }
          }
        }
      }

      if (di.item.name === "ReportTableColumn") {
        return itemCurrentRow;
      }

      if (iteratorParent.item.name === "KanbanBoard") {
        itemCurrentRow.children = this.getKanbanCurrentRows(di, iteratorParent, databindingOptions, targetTypeName);

        return itemCurrentRow;
      }

      itemCurrentRow.children = this.generateFieldsOfType(
        currentType,
        targetTypeName,
        undefined,
        undefined,
        this.props.targetCardinality
      );

      return itemCurrentRow;
    }

    return null as unknown as CascaderOptionType;
  };

  generateCardinalityConversion = (
    currentType: IKType,
    currentCardinality: ECardinality,
    targetTypeName: string,
    targetCardinality: ECardinality,
    prop?: IDisplayItemProperty,
    diComponent?: IDisplayItemComponent,
    isInlineConversion: boolean = false,
    parentParam?: IActionParameter
  ) => {
    if (currentType === undefined) {
      return [];
    }

    if ((diComponent?.name === "MultiSelect" || diComponent?.name === "Date") && prop?.name === "value") {
      targetCardinality = ECardinality.Any;
    }

    const result: CascaderOptionType[] = [];
    let propDef: IPropertyDefinition | undefined;
    if (diComponent && prop) {
      propDef = this.props.sd.getPropertyDefinition(diComponent, prop);
    }

    if (targetTypeName === "Any" && currentType.elements && currentType.elements.length > 0) {
      if (currentCardinality === ECardinality.Many) {
        // For lists, only provide cardinality converters
        result.push({
          category: ESymbolCategory.cardinalityConvertion,
          label: "First",
          cardinalityConversion: ECardinalityConversion.first,
          isInlineConversion,
          children: this.generateFieldsOfType(currentType, targetTypeName, propDef, "First", ECardinality.One)
        });

        result.push({
          category: ESymbolCategory.cardinalityConvertion,
          label: "Last",
          cardinalityConversion: ECardinalityConversion.last,
          isInlineConversion,
          children: this.generateFieldsOfType(currentType, targetTypeName, propDef, "Last", ECardinality.One)
        });

        return result;
      }
      // For single objects, provide direct access to fields
      const directFields = this.generateFieldsOfType(
        currentType,
        targetTypeName,
        propDef,
        undefined,
        targetCardinality
      );
      return directFields;
    }

    if (targetCardinality == ECardinality.Any) targetCardinality = ECardinality.One;

    if (targetCardinality === currentCardinality) {
      // one to one
      if (
        (ScreenDesignHelper.getAreTypesCompatible(currentType?.name, targetTypeName) && targetTypeName != "Unknown") ||
        (targetTypeName == "Unknown" && (!currentType?.elements || currentType?.elements.length == 0))
      ) {
        return [];
      }

      if (targetCardinality === ECardinality.Many) {
        // if many to many with primitive target type or with object parent
        if (
          (knownTypes.includes(targetTypeName.toLowerCase()) &&
            currentType.elements &&
            currentType.elements.length > 0) ||
          (!knownTypes.includes(targetTypeName.toLowerCase()) && parentParam?.isObject)
        ) {
          result.push({
            category: ESymbolCategory.cardinalityConvertion,
            label: "First",
            cardinalityConversion: ECardinalityConversion.first,
            isInlineConversion
          } as CascaderOptionType);

          result.push({
            category: ESymbolCategory.cardinalityConvertion,
            label: "Last",
            cardinalityConversion: ECardinalityConversion.last,
            isInlineConversion
          } as CascaderOptionType);
        }
      } else {
        return this.generateFieldsOfType(currentType, targetTypeName, propDef, undefined, targetCardinality);
      }
    }

    if (targetCardinality == ECardinality.One && currentCardinality === ECardinality.Many) {
      // many-to-one
      if (!knownTypes.includes(targetTypeName.toLowerCase()) && knownTypes.includes(currentType.name.toLowerCase()))
        return [];

      result.push({
        category: ESymbolCategory.cardinalityConvertion,
        label: "First",
        cardinalityConversion: ECardinalityConversion.first,
        isInlineConversion
      } as CascaderOptionType);

      result.push({
        category: ESymbolCategory.cardinalityConvertion,
        label: "Last",
        cardinalityConversion: ECardinalityConversion.last,
        isInlineConversion
      } as CascaderOptionType);

      if (diComponent && prop) {
        const propDef = this.props.sd.getPropertyDefinition(diComponent, prop);
        if (
          propDef &&
          (propDef.isOptionsProp ||
            propDef.isTableOptionsProp ||
            propDef.isGoogleMapsOptionsProp ||
            propDef.isTreeOptionsProp ||
            propDef.isCalendarOptionsProp ||
            propDef.isMenuOptionsProp ||
            propDef.isBreadcrumbOptionsProp ||
            propDef?.isKanbanBoardLaneOptionsProp === true ||
            propDef?.isKanbanBoardCardOptionsProp === true ||
            propDef?.isNodeTreeNodeOptionsProp === true)
        ) {
          result.push({
            category: ESymbolCategory.cardinalityConvertion,
            label: "Selected",
            cardinalityConversion: ECardinalityConversion.selected,
            isKanbanLanePropSelected: propDef?.isKanbanBoardLaneOptionsProp,
            isKanbanCardPropSelected: propDef?.isKanbanBoardCardOptionsProp
          } as CascaderOptionType);
        }
      }
    } else if (
      currentCardinality === ECardinality.One &&
      targetCardinality === ECardinality.Many &&
      currentType.elements &&
      currentType.elements.length > 0
    ) {
      return this.generateFieldsOfType(currentType, targetTypeName, propDef, undefined, targetCardinality);
    } else {
      // TODO: one-to-many desteklenmiyor
    }

    if (this.props.parameter?.isObject && targetCardinality !== ECardinality.Many) {
      return result;
    }
    for (let i = 0; i < result.length; i++) {
      result[i].children = this.generateFieldsOfType(
        currentType,
        targetTypeName,
        propDef,
        result[i].label?.toString(),
        this.props.targetCardinality
      );
    }

    return result.filter((i) => i.children && i.children.length > 0);
  };

  generateFieldsOfType = (
    currentType: IKType,
    targetType: string,
    propDef?: IPropertyDefinition,
    parentLabel?: string,
    targetCardinality?: ECardinality
  ) => {
    const isNestedDataBindingEnabled: boolean = this.getIsNestedDataBindingEnabled();

    if (!currentType) return [];

    if (!currentType?.elements) return ScreenDesignHelper.generateTypeConversion(currentType.name, targetType);

    const result = [] as CascaderOptionType[];

    for (let i = 0; i < currentType.elements.length; i++) {
      if (
        targetCardinality !== undefined &&
        targetCardinality === ECardinality.Many &&
        !currentType.elements[i].isList
      ) {
        continue;
      }

      if (!currentType.elements[i].type) {
        continue;
      }

      if (targetCardinality === ECardinality.Any) {
        targetCardinality = ECardinality.One;
      }

      const fieldOfTypeItem = this.generateItemLabel(
        currentType.elements[i].name,
        currentType.elements[i].type.toString(),
        currentType.elements[i].isList ? ECardinality.Many : ECardinality.One,
        ESymbolCategory.fieldOfType,
        undefined,
        currentType.elements[i]
      );

      const elementTypeModel = currentType.elements[i].elementTypeModel;

      if (targetType === "Any") {
        if (currentType.elements[i].isList) {
          const directField = { ...fieldOfTypeItem };
          delete directField.children;
          result.push(directField);

          fieldOfTypeItem.children = [
            {
              category: ESymbolCategory.cardinalityConvertion,
              label: "First",
              cardinalityConversion: ECardinalityConversion.first,
              isInlineConversion: true
            },
            {
              category: ESymbolCategory.cardinalityConvertion,
              label: "Last",
              cardinalityConversion: ECardinalityConversion.last,
              isInlineConversion: true
            }
          ];

          if (elementTypeModel?.elements && elementTypeModel.elements.length > 0) {
            fieldOfTypeItem.children.forEach((converter) => {
              converter.children = this.generateFieldsOfType(
                elementTypeModel,
                targetType,
                propDef,
                converter.label?.toString(),
                ECardinality.One
              );
            });
          } else {
            fieldOfTypeItem.children.forEach((converter) => {
              converter.children = ScreenDesignHelper.generateTypeConversion(fieldOfTypeItem.fieldtype, targetType);
            });
          }

          result.push(fieldOfTypeItem);
          continue;
        } else if (elementTypeModel?.elements && elementTypeModel.elements.length > 0) {
          const directField = { ...fieldOfTypeItem };
          delete directField.children;
          result.push(directField);

          fieldOfTypeItem.children = this.generateFieldsOfType(
            elementTypeModel,
            targetType,
            propDef,
            fieldOfTypeItem.label?.toString(),
            targetCardinality
          );
          if (fieldOfTypeItem.children && fieldOfTypeItem.children.length > 0) {
            result.push(fieldOfTypeItem);
          }
          continue;
        }
      }

      if (
        isNestedDataBindingEnabled &&
        currentType.elements[i].isList &&
        currentType.elements[i].elementTypeModel?.elements
      ) {
        fieldOfTypeItem.children = this.generateCardinalityConversion(
          currentType.elements[i].elementTypeModel!,
          ECardinality.Many,
          targetType,
          targetCardinality || ECardinality.One,
          undefined,
          undefined,
          true
        );
      } else if (
        isNestedDataBindingEnabled &&
        knownTypes.includes(targetType.toLowerCase()) &&
        currentType.elements[i].isList &&
        targetCardinality === ECardinality.One
      ) {
        fieldOfTypeItem.children = [
          {
            category: ESymbolCategory.cardinalityConvertion,
            label: "First",
            cardinalityConversion: ECardinalityConversion.first,
            isInlineConversion: true
          },
          {
            category: ESymbolCategory.cardinalityConvertion,
            label: "Last",
            cardinalityConversion: ECardinalityConversion.last,
            isInlineConversion: true
          }
        ];

        fieldOfTypeItem.children.forEach((converter) => {
          converter.children = ScreenDesignHelper.generateTypeConversion(fieldOfTypeItem.fieldtype, targetType);
        });
      } else if (
        !currentType.elements[i].isList &&
        (currentType.elements[i].elementTypeModel?.elements?.length || 0) > 0
      ) {
        fieldOfTypeItem.children = this.generateFieldsOfType(
          currentType.elements[i].elementTypeModel!,
          targetType,
          propDef,
          fieldOfTypeItem.label?.toString(),
          targetCardinality
        );
      } else if (!ScreenDesignHelper.getAreTypesCompatible(fieldOfTypeItem.fieldtype, targetType)) {
        fieldOfTypeItem.children = ScreenDesignHelper.generateTypeConversion(fieldOfTypeItem.fieldtype, targetType);
        if (fieldOfTypeItem.children.length == 0) continue;
      }
      if (
        (fieldOfTypeItem.children && fieldOfTypeItem.children.length > 0) ||
        fieldOfTypeItem.fieldtype === targetType ||
        targetType === "Unknown" ||
        targetType === "Any" ||
        ScreenDesignHelper.getAreTypesCompatible(fieldOfTypeItem.fieldtype, targetType)
      )
        result.push(fieldOfTypeItem);
    }

    if (parentLabel && parentLabel === "Selected") {
      if (propDef && propDef.isTableOptionsProp === true) {
        const fieldOfTypeItem = {
          category: ESymbolCategory.fieldOfType,
          label: "Row Index",
          fieldtype: "k_rowIndex",
          field: {
            name: "k_rowIndex"
          }
        } as CascaderOptionType;
        result.push(fieldOfTypeItem);
      }

      if (
        propDef &&
        propDef.isTableOptionsProp === true &&
        this.props.sd.project.projectType === EProjectType.Desktop
      ) {
        // mobilde desteklenmiyor
        const fieldOfTypeItem = {
          category: ESymbolCategory.fieldOfType,
          label: "Column Index",
          fieldtype: "k_columnIndex",
          field: {
            name: "k_columnIndex"
          }
        } as CascaderOptionType;
        result.push(fieldOfTypeItem);
      }
    }

    return result;
  };

  findActionDefitionById = (actionDefinitionId: string): IActionDefinitionDto => {
    return this.props.sd.actionDefinitions.filter((x) => x.id == actionDefinitionId)[0];
  };

  /**
   * Finds action definition by ID and enriches it with dynamic return type for GetFromBag/FetchFromCache actions.
   * These actions store their return type on the action instance, not the definition.
   * This method handles chained scenarios (e.g., AddToBag → GetFromBag → AddToBag → GetFromBag).
   */
  findActionDefinitionWithDynamicType = (
    actionDefinitionId: string,
    resultActionId?: string,
    actionTag?: string
  ): IActionDefinitionDto | undefined => {
    const definition = this.findActionDefitionById(actionDefinitionId);
    if (!definition) return undefined;

    // Check if this is a GetFromBag or FetchFromCache action that needs dynamic type resolution
    if (definition.name === "GetFromBag" || definition.name === "FetchFromCache") {
      const clonedDefinition = _.cloneDeep(definition);
      const actions = this.props.sd.getDefinedActions();
      const bagKey = actionTag;

      // Try to find the action instance by resultActionId first
      let actionInstance = resultActionId ? actions.find((a) => a.id === resultActionId) : undefined;

      // If not found by resultActionId, try to find by action label and tag
      if (!actionInstance && bagKey) {
        actionInstance = actions.find((a) => a.label === definition.name && a.tag === bagKey);
      }

      // If action instance found and has returnTypeModel with elements, use it
      if (actionInstance?.returnTypeModel?.elements?.length) {
        clonedDefinition.returnTypeModel = actionInstance.returnTypeModel as IKType;
        clonedDefinition.returnTypeName = actionInstance.returnType as string;
        clonedDefinition.returnCardinality = actionInstance.returnCardinality as ECardinality;
      } else {
        // Try to get type from bags/caches dictionary
        const dynamicTypes = definition.name === "GetFromBag" ? this.props.sd.bags : this.props.sd.caches;
        const typeInfo = dynamicTypes?.[bagKey || actionInstance?.tag || ""];

        if (typeInfo?.typeModel?.elements?.length) {
          clonedDefinition.returnTypeName = typeInfo.returnTypeName as string;
          clonedDefinition.returnCardinality = typeInfo.returnCardinality as ECardinality;
          // Handle path navigation for nested types
          if (typeInfo.path) {
            const resolvedType = this.navigateTypeModelByPath(typeInfo.typeModel as IKType, typeInfo.path);
            if (resolvedType) {
              clonedDefinition.returnTypeModel = resolvedType;
            }
          } else {
            clonedDefinition.returnTypeModel = typeInfo.typeModel as IKType;
          }
        } else {
          // Fallback: Trace through the matching AddToBag/PushToCache action to resolve the type
          const keyToResolve = bagKey || actionInstance?.tag || undefined;
          const resolvedType = this.resolveTypeFromAddAction(
            definition.name === "GetFromBag" ? "AddToBag" : "PushToCache",
            keyToResolve,
            actions
          );
          if (resolvedType?.elements?.length) {
            clonedDefinition.returnTypeModel = resolvedType;
          }
        }
      }

      if (!clonedDefinition.returnTypeModel) {
        clonedDefinition.returnTypeModel = { name: clonedDefinition.returnTypeName } as IKType;
      }

      return clonedDefinition;
    }

    return definition;
  };

  /**
   * Navigate through a type model using a dot-separated path (e.g., "ProductSales" or "Items.Details")
   */
  private navigateTypeModelByPath = (typeModel: IKType, path: string): IKType | undefined => {
    if (!typeModel || !path) return typeModel;

    const fields = path.split(".");
    let currentModel = typeModel;

    for (const fieldName of fields) {
      const fieldNameWithoutModifier = fieldName.split("[")[0];
      const field = currentModel?.elements?.find((x) => x.name === fieldNameWithoutModifier);
      if (field?.elementTypeModel) {
        currentModel = field.elementTypeModel;
      } else {
        return undefined;
      }
    }

    return currentModel;
  };

  /**
   * Resolves the type for a GetFromBag/FetchFromCache action by tracing through the matching AddToBag/PushToCache action.
   * This handles cross-screen chained scenarios where the type info might not be directly available.
   */
  private resolveTypeFromAddAction = (
    addActionLabel: string,
    bagKey: string | undefined,
    actions: IAction[]
  ): IKType | undefined => {
    if (!bagKey) return undefined;

    // Find the AddToBag/PushToCache action with the same key
    const addAction = actions.find(
      (a) => a.label === addActionLabel && a.parameters?.find((p) => p.name === "key")?.symbol?.fixedValue === bagKey
    );
    if (!addAction) return undefined;

    // Get the bagItem parameter's symbol
    const bagItemParam = addAction.parameters?.find((p) => p.name === "bagItem" || p.name === "cacheItem");
    const bagItemSymbol = bagItemParam?.symbol;
    if (!bagItemSymbol) return undefined;

    // If the bagItem has a fieldName (like "ProductSales" from "Current Row.ProductSales"),
    // we need to trace to the source action and navigate to that field
    if (bagItemSymbol.actionDefinitionId && bagItemSymbol.fieldName) {
      const sourceActionDef = this.findActionDefitionById(bagItemSymbol.actionDefinitionId);

      if (sourceActionDef?.name === "GetFromBag" || sourceActionDef?.name === "FetchFromCache") {
        // This is a chained bag/cache operation - the source is another GetFromBag/FetchFromCache
        const sourceBagKey =
          bagItemSymbol.actionDefinitionObjectPath?.split("#")[1] ||
          actions.find((a) => a.id === bagItemSymbol.resultActionId)?.tag;

        if (sourceBagKey) {
          // Look up the source bag's type in the bags dictionary
          const dynamicTypes = sourceActionDef.name === "GetFromBag" ? this.props.sd.bags : this.props.sd.caches;
          const sourceTypeInfo = dynamicTypes?.[sourceBagKey];

          if (sourceTypeInfo?.typeModel) {
            let sourceType = sourceTypeInfo.typeModel as IKType;

            // If the source has a path, navigate to it first
            if (sourceTypeInfo.path) {
              const navigated = this.navigateTypeModelByPath(sourceType, sourceTypeInfo.path);
              if (navigated) sourceType = navigated;
            }

            // Now navigate to the field (e.g., "ProductSales")
            const fieldPath = bagItemSymbol.fieldName;
            if (fieldPath) {
              const resolvedType = this.navigateTypeModelByPath(sourceType, fieldPath);
              return resolvedType;
            }

            return sourceType;
          }
        }
      } else if (sourceActionDef) {
        // Source is a regular action (like DB query) - use its returnTypeModel
        const sourceAction = actions.find((a) => a.id === bagItemSymbol.resultActionId);
        let sourceType = sourceAction?.returnTypeModel || sourceActionDef.returnTypeModel;

        if (sourceType && bagItemSymbol.fieldName) {
          const resolvedType = this.navigateTypeModelByPath(sourceType as IKType, bagItemSymbol.fieldName);
          return resolvedType;
        }
      }
    }

    return undefined;
  };

  findTypeByName = (typename: string): IKType => {
    const typeRepo = this.props.sd.types;
    return typeRepo.filter((x) => x.name == typename)[0];
  };

  findTypeById = (typeid: EPropertyType): IKType => {
    return { id: typeid, name: EPropertyType[typeid] } as IKType;
  };

  isDataBindingOptions = (di: IDisplayItem): boolean => {
    return this.getDataBindingOptions(di).length > 0;
  };

  getDataBindingOptions = (di: IDisplayItem): IDisplayItemProperty[] => {
    const props: IDisplayItemProperty[] = di.item.properties;
    const result: IDisplayItemProperty[] = [];

    props.forEach((prop: IDisplayItemProperty) => {
      let propDef: IPropertyDefinition | undefined;

      if (di.item && prop) {
        propDef = this.props.sd.getPropertyDefinition(di.item, prop);

        if (isRowSourceOptionsProperty(propDef)) {
          result.push(prop);
        }
      }
    });

    return result;
  };

  getActionTypeByPath = (
    action: IActionDefinitionDto,
    actionDefinitionObjectPath: string | undefined,
    includeLists: boolean = false
  ): IKType => {
    if (actionDefinitionObjectPath === undefined) {
      return action.returnTypeModel || ({ name: action.returnTypeName } as IKType);
    }
    const pathArr = actionDefinitionObjectPath.split(".");
    let activeModel: IKType | undefined = _.clone(action?.returnTypeModel);
    let result: IKTypeField[] = [];
    if (pathArr?.length > 0) {
      pathArr.forEach((pathItem, index) => {
        if (index > 0) {
          const el = activeModel?.elements?.find((e) => e.name == pathItem);
          if (el?.elementTypeModel) {
            activeModel = _.clone(el?.elementTypeModel);
          } else {
            result = [];
            activeModel = undefined;
          }
        }
        if (index === pathArr?.length - 1) {
          activeModel?.elements?.forEach((element: IKTypeField) => {
            if (element.isList === true && includeLists === true) {
              result.push(_.clone(element));
            } else if (element.isList !== true) {
              result.push(_.clone(element));
            }
          });
        }
      });
    }
    if (activeModel) {
      activeModel.elements = result;
    }
    return activeModel;
  };

  getReturnTypeModelByFieldName = (returnTypeModel: IKType, fieldName: string): IKTypeField | undefined => {
    if (!fieldName) return undefined;

    const fields: string[] = fieldName.split(".");

    if (fields.length === 0) {
      return undefined;
    }

    if (fields.length === 1) {
      const fieldNameWithoutModifier: string = fieldName.split("[")[0];
      return returnTypeModel?.elements?.find((x) => x.name === fieldNameWithoutModifier);
    }

    let currentModel: IKType = _.clone(returnTypeModel);

    for (const field of fields) {
      const fieldNameWithoutModifier: string = field.split("[")[0];

      const fieldModel = currentModel?.elements?.find((x) => x.name === fieldNameWithoutModifier);

      if (fieldModel?.elementTypeModel) {
        currentModel = _.clone(fieldModel.elementTypeModel);
      }
    }

    const lastField: string = fields[fields.length - 1] as string;
    const lastFieldNameWithoutModifier: string = lastField.split("[")[0];

    return currentModel?.elements?.find((x) => x.name === lastFieldNameWithoutModifier);
  };

  /**
   * Converts widget domain type (ITypeModel) to Kuika type (IKType) for symbol picker compatibility.
   * Handles nested types recursively to support full domain type navigation.
   */
  convertWidgetDomainTypeToKType = (typeModel: ITypeModel): IKType => {
    const kType: IKType = {
      id: 0,
      name: typeModel.name,
      elements: []
    };

    if (typeModel.elements) {
      kType.elements = typeModel.elements.map((element: ITypeElementModel) => {
        const kField: IKTypeField = {
          name: element.name,
          prefix: element.prefix,
          isList: element.isList,
          type: { id: 0, name: element.type } as IKType
        };

        // Handle nested domain types recursively
        if (element.elementTypeModel) {
          kField.elementTypeModel = this.convertWidgetDomainTypeToKType(element.elementTypeModel);
        }

        return kField;
      });
    }

    return kType;
  };

  /**
   * Looks up a widget domain type by name from the project context.
   * Supports lazy resolution - domain types are stored by name reference.
   */
  getWidgetDomainTypeAsKType = (domainTypeName: string): IKType | undefined => {
    const domainType = this.props.sd.getWidgetDomainType(domainTypeName);
    if (domainType) {
      return this.convertWidgetDomainTypeToKType(domainType);
    }
    return undefined;
  };

  calculateValueTypeByDi = (di: IDisplayItem, prop: IDisplayItemProperty): IKType => {
    let propDef: IPropertyDefinition | undefined;
    const diItem = di.item;

    if (diItem && prop) {
      propDef = this.props.sd.getPropertyDefinition(di.item, prop);
    }

    // Handle widget dynamic properties with domain types (lazy resolution)
    if (
      prop.isDynamicProperty &&
      prop.dynamicPropertyDefinition?.isDomainType &&
      prop.dynamicPropertyDefinition.domainTypeName
    ) {
      const domainType = this.getWidgetDomainTypeAsKType(prop.dynamicPropertyDefinition.domainTypeName);
      if (domainType) {
        return domainType;
      }
    }

    let action: IActionDefinitionDto | undefined;

    let actionDefinitionObjectPath: string | undefined;

    if (propDef && propDef.isValueProp) {
      // KOA-17852: Menu's value props (collapsed, selectedKey) hold component UI state, not a
      // datasource-derived value. Inheriting the dynamic `options` row type here turned
      // `Collapsed` into a bogus row-field cascade (and dropped it from the picker whenever no
      // row field matched the binding target). Let them resolve to their declared primitive
      // types via the `findTypeById(prop.selectedType)` fallback instead.
      const isMenuUiStateProp: boolean = diItem.name === "Menu";
      const optionsProp = isMenuUiStateProp
        ? undefined
        : diItem.properties.find((p) => {
            const optionsPropDef = this.props.sd.getPropertyDefinition(di.item, p);
            return (
              optionsPropDef?.isOptionsProp === true ||
              optionsPropDef?.isTreeOptionsProp === true ||
              optionsPropDef?.isCalendarOptionsProp === true ||
              optionsPropDef?.isMenuOptionsProp === true ||
              optionsPropDef?.isBreadcrumbOptionsProp === true
            );
          });

      if (optionsProp) {
        if (optionsProp.symbol.isFixed) return { name: "String" } as IKType;

        if (optionsProp.symbol.actionDefinitionId) {
          actionDefinitionObjectPath = optionsProp?.symbol?.actionDefinitionObjectPath;
          // Extract tag from actionDefinitionObjectPath (format: "actionName#tag")
          const optionsTag = actionDefinitionObjectPath?.split("#")[1];
          action = this.findActionDefinitionWithDynamicType(
            optionsProp.symbol.actionDefinitionId,
            optionsProp.symbol.resultActionId,
            optionsTag
          );
        }
      }

      if (!action && prop.symbol.actionDefinitionId) {
        actionDefinitionObjectPath = prop?.symbol?.actionDefinitionObjectPath;
        // Extract tag from actionDefinitionObjectPath (format: "actionName#tag")
        const propTag = actionDefinitionObjectPath?.split("#")[1];
        action = this.findActionDefinitionWithDynamicType(
          prop.symbol.actionDefinitionId,
          prop.symbol.resultActionId,
          propTag
        );
      }

      if (action) {
        const returnTypeModel = this.getActionTypeByPath(action, actionDefinitionObjectPath);

        if (prop.symbol.fieldName && !optionsProp) {
          const propertySdHelper = new PropertySdHelper(() => this.props.sd);
          const field = propertySdHelper.getReturnTypeModelByFields(prop.symbol.fieldName, returnTypeModel);

          if (field) {
            if (typeof field.type === "string") {
              return { name: field.type as string } as IKType;
            }
            return field.type;
          }
        }

        return returnTypeModel;
      }
    }

    if (
      propDef &&
      (propDef.isOptionsProp ||
        propDef.isTableOptionsProp ||
        propDef.isGoogleMapsOptionsProp ||
        propDef.isCalendarOptionsProp ||
        propDef.isMenuOptionsProp ||
        propDef.isBreadcrumbOptionsProp ||
        propDef.isKanbanBoardLaneOptionsProp ||
        propDef.isKanbanBoardCardOptionsProp ||
        propDef.isNodeTreeNodeOptionsProp) &&
      prop.symbol.actionDefinitionId
    ) {
      actionDefinitionObjectPath = prop?.symbol?.actionDefinitionObjectPath;
      // Extract tag from actionDefinitionObjectPath (format: "actionName#tag")
      const actionTag = actionDefinitionObjectPath?.split("#")[1];
      const action = this.findActionDefinitionWithDynamicType(
        prop.symbol.actionDefinitionId,
        prop.symbol.resultActionId,
        actionTag
      );
      if (!action) return this.findTypeByName("Unbound");

      const isNestedDataBindingEnabled: boolean = this.getIsNestedDataBindingEnabled();

      const returnTypeModel: IKType = this.getActionTypeByPath(
        action,
        actionDefinitionObjectPath,
        isNestedDataBindingEnabled
      );

      return returnTypeModel;
    }

    // todo: migration sonrası geçici commentlendi. düzenlenip açılmalı
    // if (di.databindingValue && di.databindingValue.symbol && di.databindingValue.symbol.category == ESymbolCategory.inheritedAction && di.databindingValue.symbol.fieldName) {
    //   return di.databindingValue.symbol.fields.filter((x) => x.name == di.databindingValue.symbol.fieldName)[0].type;
    // }

    // if (propDef && propDef.isValueProp && prop.selectedType === EPropertyType.image) {
    //   return this.findTypeByName("Image");
    // }

    const parents: IDisplayItem[] = this.getParents(di);

    let iteratorParents: IDisplayItem[] = parents.filter((x) => {
      return this.isDataBindingOptions(x);
    });

    if (iteratorParents.length > 1) {
      iteratorParents = this.sortDisplayItemsByHierarchy(iteratorParents);

      const iteratorsIteratorParent: IDisplayItem | undefined = iteratorParents[iteratorParents.length - 2];
      const iteratorParent: IDisplayItem | undefined = iteratorParents[iteratorParents.length - 1];

      if (iteratorsIteratorParent && iteratorParent) {
        const databindingOptions: IDisplayItemProperty[] = this.getDataBindingOptions(iteratorsIteratorParent);

        if (databindingOptions.length > 0) {
          let currentType: IKType = this.calculateValueTypeByDi(iteratorsIteratorParent, databindingOptions[0]);

          const iteratorParentDataBindingOptions: IDisplayItemProperty[] = this.getDataBindingOptions(iteratorParent);

          const returnType: IKTypeField | undefined = this.getReturnTypeModelByFieldName(
            currentType,
            iteratorParentDataBindingOptions[0].symbol.fieldName
          );

          if (returnType?.elementTypeModel) {
            currentType = returnType.elementTypeModel;
          }

          return currentType;
        }
      }
    }

    return this.findTypeById(prop.selectedType);
  };

  getIsDiContainsDi = (di: IDisplayItem, targetDi: IDisplayItem): boolean => {
    if (di.id === targetDi.id) return true;

    if (di.children) {
      for (let i = 0; i < di.children.length; i++) {
        if (this.getIsDiContainsDi(di.children[i], targetDi)) return true;
      }
    }

    return false;
  };

  sortDisplayItemsByHierarchy = (displayItems: IDisplayItem[]): IDisplayItem[] => {
    return displayItems.sort((a, b) => {
      return this.getIsDiContainsDi(a, b) ? -1 : 1;
    });
  };

  calculateCardinalityByRendererNameAndVariableName = (
    prop: IDisplayItemProperty,
    diComponent?: IDisplayItemComponent
  ): ECardinality => {
    // KOA-17641/KOA-17643: a MultiSelect's value must bind the same way regardless of the
    // declared value type. selectedType=identifier otherwise flips the modeled cardinality to
    // Many, which funnels the binding through a many-to-one First/Last conversion that
    // String-typed values never see — and whose generated read is unusable at runtime.
    if (diComponent?.name === "MultiSelect" && prop.name === "value") return ECardinality.One;

    if ([EPropertyType.object, EPropertyType.enum, EPropertyType.identifier].includes(prop.selectedType))
      return ECardinality.Many;

    return ECardinality.One;
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  getAllActions = (isMaster?: boolean): IAction[] => {
    const { actionDefinitions } = this.props.sd;
    const scheduledJob = this.props.sd.getSelectedSj();
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    const wfStep = this.props.sd.getSelectedWfStep();
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();

    // Document Parameters: the source actions the right panel added are the available results, so a parameter's
    // value is picked the same way any other action result is picked (KOA-17254).
    if (this.props.sd.isDocumentParameterAuthoringActive()) {
      return (this.props.sd.documentParameterEvent?.actions ?? []).filter(isDesignerActionEnabled);
    }
    if (scheduledJob && scheduledJob.json && scheduledJob.json.actions) {
      const { json } = scheduledJob;
      return (json.actions as IAction[]).filter(isDesignerActionEnabled);
    }
    if (api && apiMethod && this.props.sd.dashboardState === DashboardState.apiManagement && apiMethod?.actions) {
      return apiMethod.actions.filter(isDesignerActionEnabled);
    }
    if (ds?.datasourceType === EDatasourceType.BleService && bleMethod && bleMethod.bluetoothInfo?.actions) {
      return bleMethod.bluetoothInfo.actions.filter(isDesignerActionEnabled);
    }
    if (this.props.sd.dashboardState === DashboardState.workFlow) {
      if (wfStep && wfStep?.actions) return wfStep.actions.filter(isDesignerActionEnabled);
      return [];
    }
    let sd: IScreenDesign | undefined = this.props.sd.getScreenDesign();

    if (isMaster) {
      sd = this.props.sd.masterSd;
    }

    const allDi: IDisplayItem[] = [];

    if (sd) {
      this.fillAllDisplayItemsRecursive(sd, allDi);
    }

    const allEvents: IEvent[] = allDi.filter((di) => di.item.events).flatMap((di) => di.item.events as IEvent[]);
    const allActions: IAction[] = allEvents
      .filter((evt: IEvent) => evt.actions)
      .flatMap((evt: IEvent) => evt.actions)
      .filter((action: IAction) => Boolean(action))
      .filter(isDesignerActionEnabled)
      .map((action: IAction) => ({ ...action, tag: action.tag || null }));

    const ifThenElseActionDef = actionDefinitions.find(
      (action) => action.actionType === EDatasourceType.SystemJSFunctions && action.name === "IfThenElse"
    );
    const forEachActionDef = actionDefinitions.find(
      (action) => action.actionType === EDatasourceType.SystemJSFunctions && action.name === "ForEach"
    );
    const recurringActionsActionDef = actionDefinitions.find(
      (action) => action.actionType === EDatasourceType.SystemCSFunctions && action.name === "RecurringActions"
    );

    const controlFlowActions = allActions.filter(
      (action) =>
        ifThenElseActionDef?.id?.toLowerCase() === action.actionDefinitionId?.toLowerCase() ||
        forEachActionDef?.id?.toLowerCase() === action.actionDefinitionId?.toLowerCase() ||
        recurringActionsActionDef?.id?.toLowerCase() === action.actionDefinitionId?.toLowerCase()
    );

    const regularActions = allActions.filter(
      (action) =>
        ifThenElseActionDef?.id?.toLowerCase() !== action.actionDefinitionId?.toLowerCase() &&
        forEachActionDef?.id?.toLowerCase() !== action.actionDefinitionId?.toLowerCase() &&
        recurringActionsActionDef?.id?.toLowerCase() !== action.actionDefinitionId?.toLowerCase()
    );

    const nestedActions: IAction[] = [];

    for (const action of controlFlowActions) {
      nestedActions.push(...this.getNestedActions(action));
    }

    const combinedActions: IAction[] = [...regularActions, ...nestedActions];

    return _.uniqWith(combinedActions, (a, b) => a.label === b.label && a.tag === b.tag);
  };

  private isGetMyTasksSortParam = (): boolean => {
    const isSortBy = this.props.parameter?.name === "SortBy";
    if (!isSortBy) return false;

    if (this.props.action?.label === "GetMyTasks") return true;

    const def = this.props.sd.actionDefinitions.find((ad) => ad.id === this.props.action?.actionDefinitionId);
    return def?.name === "GetMyTasks";
  };

  private getMyTasksSortKeys = (): { label: string; value: string }[] => [
    { label: "Process No", value: "processno" },
    { label: "Task No", value: "taskno" },
    { label: "Due Date", value: "duedate" },
    { label: "Step Start Date (desc)", value: "stepstartdate" },
    { label: "Process Start Date (desc)", value: "processstartdate" },
    { label: "Process Name", value: "processname" },
    { label: "Assigned", value: "assigned" },
    { label: "Overdue", value: "overdue" }
  ];

  getParents = (di: IDisplayItem): IDisplayItem[] => {
    const returnArray: IDisplayItem[] = [];
    let currentDI = di;

    while (currentDI.parentId) {
      // const parent: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemBykuikaid(
      //   currentDI.parentId.toString(),
      //   this.props.sd.getScreenDesign().displayItems
      // );
      const parent = this.props.sd.screenDesignDictionary[currentDI.parentId];

      if (parent) {
        returnArray.push(parent);
        currentDI = parent;
      }
    }

    return returnArray;
  };

  toCamelCase = (str: string) => {
    if (!str) return "";
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, _index) => {
      if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
      return match.toUpperCase();
    });
  };

  /** Appends the step's inline marker to the field path — `<field>[first]` — which
   *  `parseElementFieldPath` (`api-builder-model.ts`) reads back when a List of Objects binding
   *  is rewritten. Change one, change the other. */
  addCardinalityConverterToSymbol = (symbol: ISymbol, cardinalityConverter: string): void => {
    if (symbol.fieldName !== undefined && symbol.fieldName !== "") {
      symbol.fieldName += `[${cardinalityConverter}]`;
    } else {
      symbol.fieldName = `[${cardinalityConverter}]`;
    }
  };

  addFieldNameToSymbol = (symbol: ISymbol, fieldName: string): void => {
    if (symbol.fieldName !== undefined && symbol.fieldName !== "") {
      symbol.fieldName += `.${fieldName}`;
    } else {
      symbol.fieldName = fieldName;
    }
  };

  buildSymbolFromSelectedOptions = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    if (!selectedOptions || selectedOptions.length == 0) return null;
    const symbol: ISymbol = {} as ISymbol;
    symbol.description = "";
    symbol.valuePlaceholderText = this.props.symbol?.valuePlaceholderText;

    let parentActionId: string | undefined;
    selectedOptions.forEach((node) => {
      if (![ESymbolCategory.typeConvertion, ESymbolCategory.cardinalityConvertion].includes(node.category)) {
        if (symbol.category !== ESymbolCategory.current && symbol.category !== ESymbolCategory.inputMethod) {
          symbol.category = node.category;
        }
      }

      symbol.tableLabelSourceType =
        this.props.symbol && this.props.symbol.tableLabelSourceType
          ? this.props.symbol.tableLabelSourceType
          : undefined;
      switch (node.category) {
        case ESymbolCategory.displayItem:
          symbol.label = "Component";
          symbol.diId = node.diId;
          symbol.description += `[diId:${node.diId ? node.diId : ""}]`;
          break;

        case ESymbolCategory.displayItemProp:
          symbol.label = "Property Of Component";
          // KOA-18185: flatten possible ReactNode labels — diPropName must be the prop name text.
          symbol.diPropName = getDesignerSymbolPickerOptionLabel(node);
          symbol.description += ".";
          symbol.description += symbol.diPropName ? symbol.diPropName : "";

          symbol.currentType = node.domainType?.name;
          if (!symbol.currentType) {
            symbol.currentType = node.domainType;
          }
          // if (!symbol.currentType && symbol.diId)
          //   symbol.currentType = this.calculateValueTypeByDi(di, prop),

          symbol.targetType = node.domainType?.name;
          if (!symbol.targetType) {
            symbol.targetType = node.domainType;
          }
          // if (!symbol.targetType && symbol.diId)
          //   symbol.targetType = this.calculateValueTypeByDi();

          symbol.currentCardinality = node.cardinality;
          symbol.targetCardinality = node.cardinality;
          break;

        case ESymbolCategory.actionResult:
          symbol.label = "Action Result";
          symbol.actionDefinitionId = node.actionDefinition?.id;
          symbol.description += `[${_.get(node, "label.props.children.key", "")}]`;
          symbol.currentType = node?.domainType?.name ? node.domainType.name : "Unknown";
          symbol.targetType = node?.domainType?.name ? node.domainType.name : "Unknown";
          symbol.resultActionId = node.resultActionId;
          // symbol.converterOfCardinality = node.converterOfCardinality;
          // symbol.currentType = node.domainType?.name;
          // symbol.targetType = node.domainType?.name;
          break;

        case ESymbolCategory.screenInput:
          symbol.label = "Screen Input";
          symbol.inputName = node.inputName;
          symbol.description += node.inputName ? node.inputName : "";
          symbol.currentType = node.domainType;
          symbol.targetType = node.domainType;
          break;

        case ESymbolCategory.documentParameterInput:
          // Same shape as a screen input: the name is the whole reference, resolved per resolve call.
          symbol.label = "Document Input";
          symbol.inputName = node.inputName;
          symbol.description += node.inputName ? node.inputName : "";
          symbol.currentType = node.domainType;
          symbol.targetType = node.domainType;
          break;

        case ESymbolCategory.inputMethod:
          symbol.label = "Input Method";
          symbol.inputName = node.inputName;
          symbol.description += node.inputName ? node.inputName : "";
          symbol.currentType = node.domainType;
          symbol.targetType = node.domainType;
          break;

        case ESymbolCategory.postData:
          symbol.label = "Post Data";
          symbol.description = "postData (IFormCollection)";
          symbol.currentType = "IFormCollection" as BackEndValueType;
          symbol.targetType = "IFormCollection" as BackEndValueType;
          break;

        case ESymbolCategory.default:
          symbol.label = "Default Value";
          // KOA-18185: node.label may be a ReactNode (e.g. a testid/render wrapper around the
          // text). A raw toString() then persists "[object Object]" as the default name, which
          // no code generator can resolve. Flatten to the visible text instead.
          symbol.defaultValueName = getDesignerSymbolPickerOptionLabel(node);
          symbol.description += getDesignerSymbolPickerOptionLabel(node);
          symbol.currentType = node.toType;
          symbol.targetType = node.toType;
          symbol.valuePlaceholderText =
            this.props.symbol && this.props.symbol.valuePlaceholderText
              ? this.props.symbol.valuePlaceholderText
              : undefined;
          break;

        case ESymbolCategory.bag:
          symbol.label = "Value From Bag";
          // KOA-18185: flatten possible ReactNode labels — bagName must be the bag's text name.
          symbol.bagName = getDesignerSymbolPickerOptionLabel(node);
          symbol.description = symbol.bagName ? symbol.bagName : "";
          break;

        case ESymbolCategory.current:
          symbol.label = "Current Row";
          symbol.description = symbol?.label ? symbol.label.toString() : "";
          symbol.diId = node.diId;
          if (node.isKanbanLanePropSelected) {
            symbol.fieldName = "laneData?";
          } else if (node.isKanbanCardPropSelected) {
            symbol.fieldName = "cardData?";
          }
          break;

        case ESymbolCategory.uploadedImages:
          symbol.label = "Uploaded Images";
          symbol.description = "";
          symbol.isFixed = node.isFixed;
          symbol.fixedValue = "";
          symbol.description = "";
          break;

        case ESymbolCategory.roleList: {
          // The role's name is the value. Flattened like the default/bag cases (KOA-18185): the
          // picker wraps every label in a test-id span, and raw it persisted an element.
          const roleName: string = getDesignerSymbolPickerOptionLabel(selectedOptions[1]);
          symbol.label = "Fixed Value";
          symbol.description = roleName;
          symbol.isFixed = true;
          symbol.fixedValue = roleName;
          if (this.props.targetTypeName === "String") {
            symbol.category = ESymbolCategory.fixed;
          }
          symbol.currentType = "String";
          break;
        }

        case ESymbolCategory.icons:
          symbol.label = "Icons";
          symbol.description = "";
          break;

        case ESymbolCategory.cardinalityConvertion:
          symbol.cardinality = ECardinality.One;
          if (node.isKanbanLanePropSelected) {
            symbol.fieldName = "laneData?";
          } else if (node.isKanbanCardPropSelected) {
            symbol.fieldName = "cardData?";
          }
          if (!node.isInlineConversion && node.cardinalityConversion !== undefined) {
            symbol.description += `.${ECardinalityConversion[node.cardinalityConversion]}`;
            symbol.converterOfCardinality = node.cardinalityConversion;
            break;
          }

          if (node.cardinalityConversion !== undefined) {
            symbol.description += `.${ECardinalityConversion[node.cardinalityConversion]}`;
            this.addCardinalityConverterToSymbol(symbol, ECardinalityConversion[node.cardinalityConversion]);
          } else {
            const conversionLabel: string = getDesignerSymbolPickerOptionLabel(node);
            symbol.description += ".";
            symbol.description += conversionLabel;

            if (conversionLabel !== "") {
              this.addCardinalityConverterToSymbol(symbol, conversionLabel.toLowerCase());
            }
          }

          break;

        case ESymbolCategory.typeConvertion:
          symbol.converterOfType = node.typeConversion;
          symbol.description += ".";
          symbol.description += symbol.converterOfType ? symbol.converterOfType : "";

          break;

        case ESymbolCategory.fixed:
          if (node.isCustomSortKey) {
            symbol.label = "Fixed Value";
            symbol.isFixed = true;
            symbol.fixedValue = node.sortKey;
            symbol.description = (node.label as string) ?? "";
            symbol.currentType = "String";
            symbol.targetType = "String";
            break;
          }
          const sd: IScreenDesign = this.props.sd.getScreenDesign();
          let di = this.props.targetDI;
          if (!di) di = this.props.sd.getSelectedDisplayItem(sd);
          const value = di?.item.name;
          const shouldValueBind =
            this.props.action === undefined &&
            di?.item.name === "Label" &&
            (this.props.sd.expandedProperties[0] === "label" || this.props.sd.expandedProperties[0] === "value");
          symbol.label = "Fixed Value";
          symbol.isFixed = node.isFixed;
          // The boolean `false` seed (KOA-6235) must come from the target's DECLARED
          // type. The outgoing symbol's targetType describes the binding being
          // removed — after a boolean Process Parameter was bound to a string prop
          // (e.g. Checkbox/Label label), it still says "Boolean" and the prop got
          // stuck rendering "False" (KOA-17927). targetTypeName is missing or
          // "Unknown" in a few picker contexts; only there fall back to the symbol.
          const declaredTypeName: string | undefined = this.props.targetTypeName?.toLowerCase();
          let effectiveTypeName: string | undefined = declaredTypeName;
          if (!effectiveTypeName || effectiveTypeName === "unknown") {
            effectiveTypeName = this.props.symbol?.targetType?.toLowerCase();
          }
          if (effectiveTypeName === "boolean") {
            symbol.fixedValue = false;
          } else if (shouldValueBind) {
            symbol.fixedValue = value;
          } else {
            symbol.fixedValue = "";
          }
          symbol.description = "";
          break;

        case ESymbolCategory.condition: {
          // KOA-17739: seed a ready-to-edit skeleton — an empty AND group plus both branches
          // defaulted exactly like a freshly bound parameter (fixed "", false or 0 by type).
          const conditionTargetType: BackEndValueType = (this.props.targetTypeName ?? "String") as BackEndValueType;
          symbol.label = "Condition";
          symbol.description = "";
          symbol.currentType = conditionTargetType;
          symbol.targetType = conditionTargetType;
          symbol.conditionalValue = {
            condition: { conditionSet: { conditions: [], type: ConditionSetType.and } },
            trueSymbol: KMainFunctions.getDefaultSymbol(conditionTargetType),
            falseSymbol: KMainFunctions.getDefaultSymbol(conditionTargetType)
          };
          break;
        }

        case ESymbolCategory.actionField:
          // Handle table fields from generic select (calculated fields)
          if (node.fieldName) {
            // Table field from generic select - use fieldName directly
            symbol.fieldName = node.fieldName;
            symbol.label = getDesignerSymbolPickerOptionLabel(node) || node.fieldName;
            symbol.description = node.description?.toString() || node.fieldName;
            symbol.currentType = node.currentType || node.fieldType || node.toType;
            symbol.targetType = node.targetType || node.fieldType || node.toType;
            symbol.fieldType = node.fieldType || node.toType;
            // Ensure label and description are set
            if (!symbol.label || symbol.label === "") {
              symbol.label = node.fieldName;
            }
            if (!symbol.description || symbol.description === "") {
              symbol.description = node.fieldName;
            }
          } else if (node.field) {
            // Handle regular action fields
            this.addFieldNameToSymbol(symbol, node.field?.name ?? "");
            const labelKey = _.get(node, "label.props.children.key", "");
            symbol.currentType = node.field?.type;
            symbol.targetType = node.field?.type;
            symbol.description += ".";
            if (labelKey) {
              symbol.description += labelKey;
            } else if (typeof node.label === "string") {
              symbol.description += node.label;
            } else {
              symbol.description += node.field?.name;
            }
            if (!symbol.currentType) {
              symbol.currentType = "Unknown";
            }
            if (!symbol.targetType) {
              symbol.targetType = "Unknown";
            }
          }
          parentActionId = parentActionId == undefined ? node.parentActionId : parentActionId;
          break;

        case ESymbolCategory.fieldOfType:
          this.addFieldNameToSymbol(symbol, node.field?.name ?? "");
          const labelKey = _.get(node, "label.props.children.key", "");
          symbol.currentType = node.field?.type;
          symbol.targetType = node.field?.type;
          symbol.description += ".";
          // symbol.description += labelKey || node.field?.name;
          if (labelKey) {
            symbol.description += labelKey;
          } else if (typeof node.label === "string") {
            symbol.description += node.label;
          } else {
            symbol.description += node.field?.name;
          }
          if (!symbol.currentType) {
            symbol.currentType = "Unknown";
          }
          if (!symbol.targetType) {
            symbol.targetType = "Unknown";
          }
          parentActionId = parentActionId == undefined ? node.parentActionId : parentActionId;
          break;

        case ESymbolCategory.processParameter:
          // KOA-18185: flatten possible ReactNode labels before persisting them as text.
          symbol.fieldName = getDesignerSymbolPickerOptionLabel(node);
          symbol.fixedValue = node.field?.label;
          symbol.description = getDesignerSymbolPickerOptionLabel(node);
          symbol.inputName = node.inputName;
          symbol.currentType = node.domainType;
          symbol.targetType = node.domainType;
          symbol.fieldType = node.domainType;
          if (!symbol.currentType) {
            symbol.currentType = "Unknown";
          }
          if (!symbol.targetType) {
            symbol.targetType = "Unknown";
          }
          break;
      }
    });

    symbol.parentActionId = parentActionId;
    // if (['fixed', 'uploadedImages', 'icons'].includes(item.category)) {
    //   field.expanded = false;
    //   field.ok = true;
    // }
    return symbol;
  };

  generateItemLabel = (
    label: string,
    type: string,
    cardinality: ECardinality,
    category: ESymbolCategory,
    actionDefinition?: IActionDefinitionDto,
    field?: IKTypeField,
    resultActionProps?: {
      tag?: string | null;
      actionId?: string;
    }
  ): CascaderOptionType => {
    const visibleType = type.includes(".") ? type.split(".").pop() : type;
    const item: CascaderOptionType = {
      label: (
        // Same `symbolPickerOption-<label>` hook the cascader stamps on plain string options
        // (`SymbolPickerCascader.withOptionTestIds`), which skips a JSX label like this one.
        <div data-testid={`symbolPickerOption-${label}`}>
          <span style={{ display: "flex", justifyContent: "space-between" }} key={label}>
            <span>
              {label}
              {resultActionProps?.tag && (
                <KuikaTag type="action" label={resultActionProps.tag} style={{ marginLeft: 2 }} />
              )}
              <div style={{ fontSize: "11px", lineHeight: "5px", fontWeight: "200", display: "flex" }}>
                <span
                  style={{
                    display: cardinality === ECardinality.Many ? "block" : "none",
                    paddingRight: "3px"
                  }}
                >
                  List of
                </span>
                {visibleType}
              </div>
            </span>
          </span>
        </div>
      ),
      category,
      resultActionId: resultActionProps?.actionId,
      // The same two facts the label draws, kept as plain data as well: the label is JSX, and a
      // search row rebuilds itself from path segments rather than reusing that element (its layout
      // is a breadcrumb, not a browse row). Without these the type caption and the action tag —
      // the things that tell two same-named fields apart — existed only inside the markup and
      // vanished the moment you searched (KOA-18357).
      typeCaption: cardinality === ECardinality.Many ? `List of ${visibleType}` : visibleType,
      actionTag: resultActionProps?.tag ?? undefined
    };

    switch (category) {
      case ESymbolCategory.actionResult:
        item.actionDefinition = actionDefinition;
        item.domainType = actionDefinition?.returnTypeModel;
        break;
      case ESymbolCategory.fieldOfType:
        item.fieldtype = field?.type;
        item.field = field;
        break;
      default:
    }
    return item;
  };
}

export function serializeDesignerSymbolPickerOptions(
  options: CascaderOptionType[],
  path: number[] = []
): IDesignerSymbolPickerOption[] {
  return options.map((option, index) => {
    const currentPath = [...path, index];
    const children = Array.isArray(option.children)
      ? serializeDesignerSymbolPickerOptions(option.children, currentPath)
      : undefined;

    return {
      path: currentPath,
      label: getDesignerSymbolPickerOptionLabel(option),
      category:
        typeof option.category === "number" && ESymbolCategory[option.category] !== undefined
          ? ESymbolCategory[option.category]
          : undefined,
      description: typeof option.description === "string" ? option.description : undefined,
      currentType: getDesignerSymbolPickerTypeName(option.currentType),
      targetType: getDesignerSymbolPickerTypeName(option.targetType),
      domainType: getDesignerSymbolPickerTypeName(option.domainType),
      cardinality: option.cardinality,
      diId: typeof option.diId === "number" ? option.diId : undefined,
      inputName: typeof option.inputName === "string" ? option.inputName : undefined,
      fieldName: typeof option.fieldName === "string" ? option.fieldName : undefined,
      resultActionId: typeof option.resultActionId === "string" ? option.resultActionId : undefined,
      actionDefinitionId: typeof option.actionDefinition?.id === "string" ? option.actionDefinition.id : undefined,
      defaultValueName:
        typeof option.category === "number" &&
        option.category === ESymbolCategory.default &&
        typeof option.label === "string"
          ? (option.label as string)
          : undefined,
      isLeaf: !children || children.length === 0,
      children
    };
  });
}

export function getDesignerSymbolPickerSelectedOptionsByPath(
  options: CascaderOptionType[],
  optionPath: number[]
): CascaderOptionType[] | null {
  if (!Array.isArray(optionPath) || optionPath.length === 0) {
    return null;
  }

  const selectedOptions: CascaderOptionType[] = [];
  let currentLevel: CascaderOptionType[] | undefined = options;

  for (const index of optionPath) {
    if (!Array.isArray(currentLevel) || index < 0 || index >= currentLevel.length) {
      return null;
    }

    const selected = currentLevel[index];
    selectedOptions.push(selected);
    currentLevel = selected.children;
  }

  return selectedOptions;
}

export function getDesignerSymbolPickerOptionLabel(option: CascaderOptionType): string {
  return (
    flattenDesignerSymbolPickerNode(option.label) || option.field?.name || option.inputName || option.description || ""
  );
}

/**
 * The option's own name, without the "List of <Type>" caption that `generateItemLabel` bakes into the
 * label — `getDesignerSymbolPickerOptionLabel` flattens every text node under it and so returns
 * "getDokumanById List of Dokuman", which is right for the bindables manifest and useless for search.
 *
 * Read off the label's React `key`, the same contract `buildSymbolFromSelectedOptions` uses; first
 * text node, then the option's name-bearing fields, as fallbacks.
 */
export function getDesignerSymbolPickerOptionName(option: CascaderOptionType): string {
  return (
    extractDesignerSymbolPickerNodeName(option.label) ||
    option.field?.name ||
    option.inputName ||
    option.fieldName ||
    option.description ||
    ""
  );
}

function extractDesignerSymbolPickerNodeName(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (!React.isValidElement(value)) {
    return "";
  }
  if (typeof value.key === "string" && value.key.length > 0) {
    return value.key;
  }

  const children: unknown = (value as React.ReactElement<{ children?: unknown }>).props?.children;
  if (!Array.isArray(children)) {
    return extractDesignerSymbolPickerNodeName(children);
  }
  for (const child of children) {
    const name: string = extractDesignerSymbolPickerNodeName(child);
    if (name) {
      return name;
    }
  }
  return "";
}

function getDesignerSymbolPickerTypeName(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  const obj = value as Record<string, unknown>;
  if (typeof obj.name === "string") return obj.name;
  if (typeof obj.type === "string") return obj.type;
  return undefined;
}

function flattenDesignerSymbolPickerNode(value: unknown): string {
  if (value == null || value === false) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => flattenDesignerSymbolPickerNode(item))
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (React.isValidElement(value)) {
    const text = flattenDesignerSymbolPickerNode((value as React.ReactElement<{ children?: unknown }>).props?.children);
    if (text) return text;
    if (typeof value.key === "string") return value.key;
    if (typeof value.key === "number") return String(value.key);
  }
  return "";
}
